import { buildInterviewCancellationMessage } from '../../utils/interviewMessages';
import { systemMessage } from '../../utils/localizedSystemMessages';
import { getUiLocale } from '../../utils/uiTranslation';
import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ref, get, set, push, update, onValue, off } from 'firebase/database';
import { database } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import InterviewScheduler from '../InterviewScheduler';
import { removeConnectionOptimized, parseConnectionKey } from './DatabaseOptimizer';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { emitAtsAnalyticsEvent } from '../../services/atsAnalyticsService';
import { buildActorAnalyticsContext } from '../../utils/analyticsEntityFields';
import ApplicantDetailsPopupV2 from './ApplicantDetailsPopupV2';
import { assessmentAnswerText, assessmentQuestionEvaluation } from './assessmentAnswerUtils';


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD;

const ApplicantDetailsPopup = ({
  applicantId,
  connectionKey: propConnectionKey,
  onClose,
  userUID,
  onApplicantRemoved,
  pendingApprovals,
  onViewApprovalDetails,
  selectedJobId,
  selectedJobDescription,
  selectedJobDepartment,
  workflowStages,
  dynamicSubstages,
  aiAssessmentSubstages,
  initialTab = 'cv',
  tutorialMode = false,
  tutorialData = null
}) => {
  useUiLanguage();
  const { t, i18n } = useTranslation('applicants');
  const { isRTL } = useLanguage();
  const { currentUser } = useAuth();
  const [applicantData, setApplicantData] = useState({});
  const [isApplicantLoading, setIsApplicantLoading] = useState(true);
  const [applicantConnectionData, setApplicantConnectionData] = useState({});
  const [applicantIndexData, setApplicantIndexData] = useState({});
  const [candidateEvaluation, setCandidateEvaluation] = useState(null);
  const [loadingCandidateEvaluation, setLoadingCandidateEvaluation] = useState(true);
  const [applicantEmail, setApplicantEmail] = useState('');
  const [candidateAnalysis, setCandidateAnalysis] = useState('');
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [activeAnalysisTab, setActiveAnalysisTab] = useState('summary');
  const [showScorecard, setShowScorecard] = useState(false);
  // Scorecard state (per-user ratings)
  const [selectedRating, setSelectedRating] = useState(''); // current user's rating
  const [teamRatings, setTeamRatings] = useState({}); // { uid: { rating, userEmail, updatedAt } }
  // Team directory for avatars and names
  const [teamDirectory, setTeamDirectory] = useState({
    byUid: {}, // { uid: { uid, email, name, photoUrl, isOwner } }
    byEmail: {} // { email: { uid, email, name, photoUrl, isOwner } }
  });
  // Team chat (replaces notes)
  const [teamNotes, setTeamNotes] = useState([]);
  const [, setShowAddNote] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [currentUserEmailGlobal, setCurrentUserEmailGlobal] = useState('');
  const [actualUserEmail, setActualUserEmail] = useState(''); // Email of the actual logged-in user
  const messageBoxRef = useRef(null);
  const teamMessageBoxRef = useRef(null);
  const messagesRef = useRef(null);

  // Rating helpers
  const ratingToValue = useMemo(() => ({
    'Poor': 1,
    'Below Average': 2,
    'Average': 3,
    'Good': 4,
    'Excellent': 5
  }), []);
  const valueToLabel = useMemo(() => ({
    1: 'Poor',
    2: 'Below Average',
    3: 'Average',
    4: 'Good',
    5: 'Excellent'
  }), []);

  const ratingSummary = useMemo(() => {
    const entries = Object.values(teamRatings || {}).filter(e => e && e.rating && ratingToValue[e.rating]);
    const count = entries.length;
    if (count === 0) return { avg: null, label: null, count: 0 };
    const sum = entries.reduce((acc, e) => acc + ratingToValue[e.rating], 0);
    const avg = sum / count;
    const rounded = Math.round(avg);
    return { avg, label: valueToLabel[rounded] || null, count };
  }, [teamRatings, ratingToValue, valueToLabel]);

  const offerApproval = useMemo(() => {
    if (!pendingApprovals || !Array.isArray(pendingApprovals) || !applicantId) return null;
    const email = (applicantEmail || '').toLowerCase();
    return pendingApprovals.find((approval) => {
      const type = approval.type || approval.metadata?.type;
      if (type !== 'offer_letter') return false;
      if (approval.entityId === applicantId) return true;
      const candidates = approval.metadata?.applicants || [];
      return candidates.some((candidate) => {
        if (!candidate) return false;
        if (candidate.id && candidate.id === applicantId) return true;
        if (email && candidate.email && candidate.email.toLowerCase() === email) return true;
        return false;
      });
    }) || null;
  }, [pendingApprovals, applicantId, applicantEmail]);

  // State for tracking opened assessment sections
  const [openAssessmentSections, setOpenAssessmentSections] = useState({});

  // State for assessment responses and evaluations
  const [assessmentData, setAssessmentData] = useState({});
  const [loadingAssessmentData, setLoadingAssessmentData] = useState(true);

  // State for tracking video loading errors
  const [videoLoadErrors, setVideoLoadErrors] = useState({});
  const [privateRecordingUrls, setPrivateRecordingUrls] = useState({});
  const [evaluationRetryState, setEvaluationRetryState] = useState({});

  // Interview-related state
  const [interviews, setInterviews] = useState([]);
  const [showInterviews, setShowInterviews] = useState(false);
  const [loadingInterviews, setLoadingInterviews] = useState(false);
  const [showInterviewScheduler, setShowInterviewScheduler] = useState(false);
  const [selectedInterviewForReschedule, setSelectedInterviewForReschedule] = useState(null);
  const [selectedInterviewWorkflowStep, setSelectedInterviewWorkflowStep] = useState(null);
  const [calendarConnections, setCalendarConnections] = useState([]);
  const [companyName, setCompanyName] = useState('');
  const [contactInfo, setContactInfo] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectInProgress, setRejectInProgress] = useState(false);

  // Buddy information state
  const [buddyInfo, setBuddyInfo] = useState(null);
  const [, setLoadingBuddyInfo] = useState(false);

  // First Week Checklist state
  const [firstWeekChecklist, setFirstWeekChecklist] = useState(null);
  const [, setLoadingChecklist] = useState(false);
  const [checklistProgress, setChecklistProgress] = useState({});
  const [collapsedSections, setCollapsedSections] = useState({
    firstWeekChecklistContent: true,
    'candidate-analysis-sec': true,
    applicationResponsesContent: true,
    scorecardContent: true,
    interviewContent: true
  });

  const dateLocale = isRTL ? 'ar-EG' : undefined;

  const normalizeExternalLink = (value) => {
    if (typeof value !== 'string') return '';
    const trimmed = value.trim();
    if (!trimmed) return '';
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    if (/^[^\s]+\.[^\s]+/.test(trimmed)) return `https://${trimmed}`;
    return '';
  };

  const normalizePercentScore = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return null;
    return Math.max(0, Math.min(100, Math.round(numeric)));
  };

  const asStringList = (value) => {
    if (Array.isArray(value)) {
      return value.map((item) => String(item || '').trim()).filter(Boolean);
    }
    if (value && typeof value === 'object') {
      return Object.values(value).map((item) => String(item || '').trim()).filter(Boolean);
    }
    if (typeof value === 'string') {
      return value
        .split(/\n|•|-/)
        .map((item) => item.trim())
        .filter(Boolean);
    }
    return [];
  };

  const resolveCandidateCvLink = () => {
    const cvSources = [
      applicantData?.cv,
      applicantConnectionData?.cv,
      applicantData?.cvLink,
      applicantData?.resumeLink,
      applicantData?.resumeUrl,
      applicantConnectionData?.cvLink,
      applicantConnectionData?.resumeLink,
      applicantConnectionData?.resumeUrl
    ];

    for (const source of cvSources) {
      if (!source) continue;
      if (typeof source === 'string') return source;
      if (typeof source === 'object') {
        const link = source.Link || source.link || source.url || source.href;
        if (link) return link;
      }
    }
    return '';
  };

  const resolveJobDescriptionText = () => {
    if (typeof selectedJobDescription === 'string') return selectedJobDescription.trim();
    if (!selectedJobDescription || typeof selectedJobDescription !== 'object') return '';
    return String(
      selectedJobDescription.jobDescription
      || selectedJobDescription.description
      || selectedJobDescription.requirements
      || selectedJobDescription.details
      || ''
    ).trim();
  };

  const getStoredFitScore = () => normalizePercentScore(
    // The evaluation ledger is authoritative. The older scalar locations stay
    // as read fallbacks while existing candidate records are migrated.
    candidateEvaluation?.current?.fitScore
    ?? applicantConnectionData?.candidateEvaluation?.current?.fitScore
    ?? applicantIndexData?.fit_score
    ?? applicantIndexData?.fitScore
    ?? applicantConnectionData?.fit_score
    ?? applicantConnectionData?.fitScore
  );

  const normalizeAnalysisPayload = (payload) => {
    if (!payload) return null;
    if (payload.legacyText) return payload;
    if (typeof payload === 'string') {
      return { legacyText: payload.trim() };
    }

    const source = payload.analysis && typeof payload.analysis === 'object' ? payload.analysis : payload;
    const benchmarks = source.benchmarks && typeof source.benchmarks === 'object' ? source.benchmarks : {};

    return {
      summary: String(source.summary || source.analysisSummary || '').trim(),
      strong_points: asStringList(source.strong_points || source.strongPoints || source.strengths),
      weak_points: asStringList(source.weak_points || source.weakPoints || source.concerns || source.risks),
      interview_questions: asStringList(source.interview_questions || source.interviewQuestions || source.questions),
      benchmarks: {
        market_position: String(benchmarks.market_position || benchmarks.marketPosition || '').trim(),
        role_level_alignment: String(benchmarks.role_level_alignment || benchmarks.roleLevelAlignment || '').trim(),
        experience_benchmark: String(benchmarks.experience_benchmark || benchmarks.experienceBenchmark || '').trim(),
        evidence_quality: String(benchmarks.evidence_quality || benchmarks.evidenceQuality || '').trim(),
        benchmark_notes: asStringList(benchmarks.benchmark_notes || benchmarks.benchmarkNotes || benchmarks.notes)
      }
    };
  };

  const applicationArtifacts = useMemo(() => {
    const profile = applicantData || {};
    const connection = applicantConnectionData || {};

    const possibleResponses =
      connection.applicationResponses
      || connection.application_answers
      || connection.applicationAnswers
      || connection.customQuestionAnswers
      || connection.customQuestionsAnswers
      || connection.responses
      || profile.applicationResponses
      || profile.application_answers
      || profile.applicationAnswers
      || profile.customQuestionAnswers
      || profile.customQuestionsAnswers
      || profile.responses
      || {};

    const responseEntries = [];

    if (Array.isArray(possibleResponses)) {
      possibleResponses.forEach((entry, index) => {
        const label = String(entry?.label || entry?.question || entry?.prompt || `Question ${index + 1}`).trim();
        const answer = String(entry?.answer || entry?.response || entry?.value || '').trim();
        if (label && answer) responseEntries.push({ label, answer });
      });
    } else if (possibleResponses && typeof possibleResponses === 'object') {
      Object.entries(possibleResponses).forEach(([key, value], index) => {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          const label = String(value.label || value.question || value.prompt || key || `Question ${index + 1}`).trim();
          const answer = String(value.answer || value.response || value.value || '').trim();
          if (label && answer) responseEntries.push({ label, answer });
          return;
        }
        const answer = String(value || '').trim();
        const label = String(key || `Question ${index + 1}`).trim();
        if (label && answer) responseEntries.push({ label, answer });
      });
    }

    const possibleCoverLetter =
      connection.coverLetter
      || connection.cover_letter
      || connection.coverLetterUrl
      || connection.cover_letter_url
      || profile.coverLetter
      || profile.cover_letter
      || profile.coverLetterUrl
      || profile.cover_letter_url;

    const coverLetterUrl = typeof possibleCoverLetter === 'object'
      ? normalizeExternalLink(
        possibleCoverLetter.url
        || possibleCoverLetter.link
        || possibleCoverLetter.Link
        || possibleCoverLetter.href
      )
      : normalizeExternalLink(possibleCoverLetter);

    const portfolioCandidates = [
      connection.portfolio,
      connection.portfolioLink,
      connection.portfolio_link,
      connection.website,
      profile.portfolio,
      profile.portfolioLink,
      profile.portfolio_link,
      profile.website,
      profile.linkedin
    ];

    const portfolioLinks = Array.from(
      new Set(
        portfolioCandidates
          .map((candidate) => {
            if (candidate && typeof candidate === 'object') {
              return normalizeExternalLink(
                candidate.url || candidate.link || candidate.Link || candidate.href || ''
              );
            }
            return normalizeExternalLink(candidate);
          })
          .filter(Boolean)
      )
    );

    return {
      responseEntries,
      coverLetterUrl,
      portfolioLinks
    };
  }, [applicantData, applicantConnectionData]);


  // Starring state
  const [isStarred, setIsStarred] = useState(false);
  const [togglingStarred, setTogglingStarred] = useState(false);

  useEffect(() => {
    setIsApplicantLoading(true);
    setCandidateEvaluation(null);
    setLoadingCandidateEvaluation(true);

    if (tutorialMode && tutorialData) {
      // Tutorial mode - use provided data, don't load from Firebase
      setApplicantData({
        name: tutorialData.name || '',
        email: tutorialData.email || '',
        phone: tutorialData.phone || '',
        experience: tutorialData.experience || '',
        education: tutorialData.education || '',
        skills: tutorialData.skills || [],
        fitScore: tutorialData.fitScore || 0,
        fitReasoning: tutorialData.fitReasoning || '',
        stageId: tutorialData.stageId || 'stage-1',
        substageId: tutorialData.substageId || 'all-applicants-1',
        jobTitle: tutorialData.jobTitle || '',
        applicantType: tutorialData.applicantType || 'authenticated',
        connectionKey: tutorialData.id // Use ID as connectionKey for tutorial
      });
      setApplicantConnectionData({
        ...tutorialData,
        stageId: tutorialData.stageId || 'stage-1',
        substageId: tutorialData.substageId || 'all-applicants-1'
      });
      setCandidateEvaluation(tutorialData.candidateEvaluation || null);
      setLoadingCandidateEvaluation(false);
      setApplicantEmail(tutorialData.email || '');

      // Set AI Analysis if provided
      if (tutorialData.aiAnalysis) {
        setApplicantIndexData({ fit_score: tutorialData.fitScore });
        setCandidateAnalysis(normalizeAnalysisPayload({
          summary: tutorialData.aiAnalysis.summary,
          strong_points: tutorialData.aiAnalysis.strengths || [],
          weak_points: tutorialData.aiAnalysis.concerns || [],
          interview_questions: tutorialData.aiAnalysis.interviewQuestions || [],
          benchmarks: tutorialData.aiAnalysis.benchmarks || {}
        }));
      }

      // Set messages if provided
      if (tutorialData.messages) {
        setMessages(tutorialData.messages);
      }

      // Set team messages (notes) if provided
      if (tutorialData.teamMessages) {
        setTeamNotes(tutorialData.teamMessages);
      }

      // Set team scorecards if provided
      if (tutorialData.teamScorecards) {
        const ratingsObj = {};
        tutorialData.teamScorecards.forEach((scorecard, idx) => {
          const criterionScore = Math.max(1, Math.min(5, Math.round(Number(scorecard.rating || 0) / 2)));
          ratingsObj[`demo_user_${idx}`] = {
            rating: scorecard.rating >= 4 ? 'Excellent' : scorecard.rating >= 3 ? 'Good' : 'Average',
            ratings: {
              technicalSkills: criterionScore,
              leadership: criterionScore,
              communication: criterionScore,
              problemSolving: criterionScore,
              cultureFit: criterionScore,
              productManagement: criterionScore
            },
            userEmail: scorecard.interviewer,
            updatedAt: scorecard.timestamp
          };
        });
        setTeamRatings(ratingsObj);
      }

      // Set interviews if provided
      if (tutorialData.scheduledInterviews) {
        setInterviews(tutorialData.scheduledInterviews);
      }

      // Set assessment data if provided
      if (tutorialData.assessmentResults) {
        setAssessmentData({
          'demo_assessment': {
            score: tutorialData.assessmentResults.score,
            analysis: tutorialData.assessmentResults.analysis,
            questions: tutorialData.assessmentResults.questionBreakdown || []
          }
        });
        setLoadingAssessmentData(false);
      }

      setIsApplicantLoading(false);
      return; // Don't run Firebase loading logic
    }

    if (applicantId && userUID) {
      loadApplicantData();
      loadInterviews();
      loadCalendarConnections();
      loadCompanyData();
      getCurrentUserEmail();
      // Load team directory for avatars/names
      loadTeamDirectory();
      loadBuddyInfo();
      loadFirstWeekChecklist();
    }
  }, [applicantId, userUID, tutorialMode, tutorialData]);

  useEffect(() => {
    if (tutorialMode) return undefined;
    const connectionKey = applicantData?.connectionKey;
    if (!connectionKey) return undefined;

    setLoadingCandidateEvaluation(true);
    const evaluationRef = ref(database, `connections/${connectionKey}/candidateEvaluation`);
    const unsubscribe = onValue(
      evaluationRef,
      (snapshot) => {
        setCandidateEvaluation(snapshot.exists() ? snapshot.val() : null);
        setLoadingCandidateEvaluation(false);
      },
      (error) => {
        console.error('Could not load the candidate evaluation journey:', error);
        setCandidateEvaluation(null);
        setLoadingCandidateEvaluation(false);
      }
    );

    // Evaluation jobs finish asynchronously, so the popup listens to the
    // ledger instead of showing a stale score captured when it first opened.
    return unsubscribe;
  }, [applicantData?.connectionKey, tutorialMode]);

  // Subscribe only after the exact connection key and applicant type have been resolved.
  // This avoids listening on a guessed authenticated-user path for imported applicants.
  useEffect(() => {
    if (tutorialMode || !applicantData?.connectionKey) return undefined;
    return loadApplicantConversation();
    // The subscription is intentionally keyed to resolved connection identity only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantData?.connectionKey, applicantData?.applicantType, tutorialMode]);

  // Load starred status after connectionKey is available
  useEffect(() => {
    if (tutorialMode) return; // Skip in tutorial mode
    if (applicantData?.connectionKey && userUID) {
      loadStarredStatus();
    }
  }, [applicantData?.connectionKey, userUID, tutorialMode]);

  // Separate useEffect for getting actual user email to avoid dependency array issues
  useEffect(() => {
    if (currentUser?.uid) {
      getActualUserEmail();
    }
  }, [currentUser?.uid]);

  // Auto-resize candidate messaging textarea
  useEffect(() => {
    if (messageBoxRef.current) {
      const textarea = messageBoxRef.current;
      textarea.style.height = '40px';
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  }, [newMessage]);

  // Auto-resize team chat textarea
  useEffect(() => {
    if (teamMessageBoxRef.current) {
      const textarea = teamMessageBoxRef.current;
      textarea.style.height = '40px';
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  }, [newNoteContent]);

  const formatAnalysisText = (text) => {
    if (!text) return text;
    let formatted = text.trim();
    const sectionHeaders = [
      'Summary of Experience:', 'Strong Points:', 'Weak Points:',
      'Global Standards Assessment:', 'Verdict:', 'Interview Questions:'
    ];
    sectionHeaders.forEach(header => {
      const regex = new RegExp(`(?<!\\n\\n)${header.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'g');
      formatted = formatted.replace(regex, `\n\n${header}`);
    });
    formatted = formatted.replace(/\n{3,}/g, '\n\n');
    formatted = formatted.replace(/\n•/g, '\n• ');
    formatted = formatted.replace(/• {2,}/g, '• ');
    return formatted.trim();
  };

  const renderFormattedAnalysis = (text) => {
    if (!text) return text;
    const lines = text.split('\n');
    const sectionHeaders = [
      'Summary of Experience:', 'Strong Points:', 'Weak Points:',
      'Global Standards Assessment:', 'Verdict:', 'Interview Questions:'
    ];
    return lines.map((line, index) => {
      const isHeader = sectionHeaders.some(header => line.trim() === header);
      if (isHeader) {
        return (
          <div key={index} style={{
            fontWeight: "var(--app-weight-strong, bold)", fontSize: '17px', color: '#568ded',
            marginTop: index === 0 ? '0' : '20px', marginBottom: '8px',
            borderBottom: '2px solid #568ded', paddingBottom: '4px'
          }}>
            {line}
          </div>
        );
      }
      return line + '\n';
    }).reduce((acc, curr, index) => {
      if (typeof curr === 'string') {
        if (typeof acc[acc.length - 1] === 'string') {
          acc[acc.length - 1] += curr;
        } else {
          acc.push(curr);
        }
      } else {
        if (typeof acc[acc.length - 1] === 'string' && acc[acc.length - 1].trim()) {
          acc.push(<div key={`text-${index}`} style={{ marginBottom: '15px' }}>{acc[acc.length - 1]}</div>);
          acc[acc.length - 2] = '';
        }
        acc.push(curr);
      }
      return acc;
    }, []).filter(item => item !== '');
  };

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showTooltip && !event.target.closest('#msg-info') && !event.target.closest('.tooltip')) {
        setShowTooltip(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTooltip]);

  // Fetch assessment data (responses and evaluations)
  useEffect(() => {
    const fetchAssessmentData = async () => {
      if (!aiAssessmentSubstages || aiAssessmentSubstages.length === 0 || !applicantId || !userUID || !applicantData.connectionKey) {
        setLoadingAssessmentData(false);
        return;
      }

      setLoadingAssessmentData(true);
      const data = {};

      try {
        for (const assessment of aiAssessmentSubstages) {
          if (assessment.assessmentId) {
            const assessmentId = assessment.assessmentId;

            // The resolved key includes imports, copied profiles, and job-specific
            // connections; reconstructing it here can read another application.
            const connectionKey = applicantData.connectionKey;

            // Fetch response data
            const responseRef = ref(database, `connections/${connectionKey}/assessments/${assessmentId}/response`);
            const responseSnapshot = await get(responseRef);

            // Fetch evaluation data
            const evaluationRef = ref(database, `connections/${connectionKey}/assessments/${assessmentId}/evaluation`);
            const evaluationSnapshot = await get(evaluationRef);
            const evaluationHistoryRef = ref(database, `connections/${connectionKey}/assessments/${assessmentId}/evaluationHistory`);
            const evaluationHistorySnapshot = await get(evaluationHistoryRef);
            const evaluationStateRef = ref(database, `connections/${connectionKey}/assessments/${assessmentId}/evaluationState`);
            const evaluationStateSnapshot = await get(evaluationStateRef);

            // Fetch assessment questions
            const assessmentRef = ref(database, `companies/${userUID}/assessments/${assessmentId}`);
            const assessmentSnapshot = await get(assessmentRef);
            const assessmentDefinition = assessmentSnapshot.exists() ? assessmentSnapshot.val() : {};
            let liveReview = null;
            if (assessmentDefinition?.deliveryType === 'two-way-voice' && currentUser) {
              try {
                const authToken = await currentUser.getIdToken();
                const reviewResponse = await fetch(
                  `${BACKEND_URL}/companies/${encodeURIComponent(userUID)}/ai-interviews/${encodeURIComponent(assessmentId)}/candidates/${encodeURIComponent(applicantId)}`,
                  { headers: { Authorization: `Bearer ${authToken}` } }
                );
                if (reviewResponse.ok) liveReview = await reviewResponse.json();
              } catch (reviewError) {
                console.error('Could not load authorized two-way interview review:', reviewError);
              }
            }

            const responseVal = responseSnapshot.exists() ? responseSnapshot.val() : null;

            // FIXED: Prefer questions stored in the response (for job-varying assessments)
            // Fallback to static assessment questions if not found in response
            const responseQuestions = responseVal?.questions;
            const staticQuestions = assessmentDefinition?.questions || [];
            const questionsToUse = responseQuestions && responseQuestions.length > 0 ? responseQuestions : staticQuestions;

            data[assessment.id] = {
              assessment: { ...assessment, ...assessmentDefinition },
              response: responseVal,
              evaluation: evaluationSnapshot.exists() ? evaluationSnapshot.val() : null,
              // Each journey event points at its immutable granular report.
              // The legacy `evaluation` node is only the most recent report.
              evaluationHistory: evaluationHistorySnapshot.exists() ? evaluationHistorySnapshot.val() : {},
              evaluationState: evaluationStateSnapshot.exists() ? evaluationStateSnapshot.val() : {},
              questions: questionsToUse,
              liveReview,
              assessmentId
            };
          }
        }

        setAssessmentData(data);
      } catch (error) {
        console.error('Error fetching assessment data:', error);
      } finally {
        setLoadingAssessmentData(false);
      }
    };

    fetchAssessmentData();
  }, [
    aiAssessmentSubstages,
    applicantId,
    userUID,
    applicantData.connectionKey,
    currentUser,
    candidateEvaluation?.current?.revision,
  ]);

  /**
   * Request authorized, short-lived playback for one two-way interview session.
   * The private Storage object path is never read from Firebase in the browser.
   */
  const loadPrivateRecording = async (substageId, assessmentId, sessionId) => {
    if (!currentUser) return;
    try {
      const authToken = await currentUser.getIdToken();
      const response = await fetch(
        `${BACKEND_URL}/companies/${encodeURIComponent(userUID)}/ai-interviews/${encodeURIComponent(assessmentId)}/candidates/${encodeURIComponent(applicantId)}/recording-url`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({ sessionId })
        }
      );
      const payload = await response.json();
      if (!response.ok || !payload?.url) throw new Error(payload?.detail || 'Private recording is not ready.');
      setPrivateRecordingUrls((previous) => ({ ...previous, [substageId]: payload }));
    } catch (recordingError) {
      alert(recordingError.message || 'Could not open the private interview recording.');
    }
  };

  /**
   * Recover a transcript-backed report whose asynchronous completion was
   * interrupted after submission. The backend rechecks company, candidate,
   * assessment, and session bindings and returns the idempotent evaluation.
   */
  const retryLiveEvaluation = async (substageId, assessmentId, sessionId) => {
    if (!currentUser || !sessionId) return;
    setEvaluationRetryState((previous) => ({
      ...previous,
      [substageId]: { loading: true, error: '' },
    }));
    try {
      const authToken = await currentUser.getIdToken();
      const query = new URLSearchParams({
        companyId: userUID,
        assessmentId,
        applicantId,
      });
      const response = await fetch(
        `${BACKEND_URL}/ai-interviews/sessions/${encodeURIComponent(sessionId)}/evaluation/retry?${query.toString()}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({ sessionId }),
        }
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload?.result) {
        throw new Error(payload?.detail || 'The interview evaluation could not be generated.');
      }
      setAssessmentData((previous) => ({
        ...previous,
        [substageId]: {
          ...previous[substageId],
          evaluation: payload,
          liveReview: {
            ...(previous[substageId]?.liveReview || {}),
            evaluation: payload,
            evaluationState: { status: 'completed' },
          },
        },
      }));
      setEvaluationRetryState((previous) => ({
        ...previous,
        [substageId]: { loading: false, error: '' },
      }));
    } catch (evaluationError) {
      setEvaluationRetryState((previous) => ({
        ...previous,
        [substageId]: {
          loading: false,
          error: evaluationError.message || 'The interview evaluation could not be generated.',
        },
      }));
    }
  };

  // Load ratings (per-user) and legacy single score when connectionKey is available
  useEffect(() => {
    if (!applicantData?.connectionKey) return;
    const scoresRef = ref(database, `connections/${applicantData.connectionKey}/scores`);
    onValue(scoresRef, (snapshot) => {
      const data = snapshot.val() || {};
      setTeamRatings(data);
      if (currentUser?.uid && data[currentUser.uid]?.rating) {
        setSelectedRating(data[currentUser.uid].rating);
      }
    });

    // Legacy single score fallback (read once)
    const legacyRef = ref(database, `connections/${applicantData.connectionKey}/score`);
    get(legacyRef).then((snap) => {
      if (!currentUser?.uid) return;
      if (snap.exists() && !selectedRating) {
        setSelectedRating(snap.val());
      }
    }).catch((e) => console.error('Error loading legacy score:', e));

    return () => {
      off(scoresRef);
    };
  }, [applicantData?.connectionKey, currentUser?.uid]);

  // Subscribe to notes AFTER applicantData.connectionKey is available (works for both authenticated and imported)
  useEffect(() => {
    if (!applicantData?.connectionKey) return;
    const notesRefObj = ref(database, `connections/${applicantData.connectionKey}/notes`);
    onValue(notesRefObj, (snapshot) => {
      const notesData = snapshot.val();
      if (notesData) {
        const notesList = Object.entries(notesData)
          .map(([key, value]) => ({ id: key, ...value }))
          .sort((left, right) => new Date(right.timestamp || 0) - new Date(left.timestamp || 0));
        setTeamNotes(notesList);
      } else {
        setTeamNotes([]);
      }
    });
    return () => {
      off(notesRefObj);
    };
  }, [applicantData?.connectionKey]);

  const getCurrentUserEmail = async () => {
    try {
      const response = await fetch('https://us-central1-jobseeqr.cloudfunctions.net/getUserEmail', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ uid: userUID })
      });
      const data = await response.json();
      setCurrentUserEmailGlobal(data.email);
    } catch (error) {
      console.log("Error fetching current user email:", error);
    }
  };

  // Get the actual logged-in user's email (for team notes)
  const getActualUserEmail = async () => {
    try {
      if (!currentUser?.uid) {
        console.log("No current user available");
        return;
      }

      console.log("Fetching email for user:", currentUser.uid);
      const response = await fetch('https://us-central1-jobseeqr.cloudfunctions.net/getUserEmail', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ uid: currentUser.uid })
      });
      const data = await response.json();
      console.log("Got actual user email:", data.email);
      setActualUserEmail(data.email);
    } catch (error) {
      console.log("Error fetching actual user email:", error);
    }
  };

  // Load team directory (owner + collaborators) for avatars and display names
  const loadTeamDirectory = async () => {
    try {
      const byUid = {};
      const byEmail = {};

      // Load collaborators under the same company
      const collabRef = ref(database, 'collaborators');
      const collabSnap = await get(collabRef);
      if (collabSnap.exists()) {
        collabSnap.forEach((child) => {
          const data = child.val();
          if (data.companyId === userUID) {
            const uid = child.key;
            const email = data.email || '';
            const name = data.name || (email ? email.split('@')[0] : 'Collaborator');
            const entry = { uid, email, name, photoUrl: '', isOwner: false };
            byUid[uid] = entry;
            if (email) byEmail[email] = entry;
          }
        });
      }

      // Load owner (company) info including photo URL
      const companyRef = ref(database, `companies/${userUID}`);
      const companySnap = await get(companyRef);
      if (companySnap.exists()) {
        const comp = companySnap.val() || {};
        const ownerEmail = comp.contactEmail || '';
        const ownerName = comp.contactName || (ownerEmail ? ownerEmail.split('@')[0] : 'Admin');
        const ownerPhotoUrl = comp.ownerPhotoUrl || '';
        const ownerEntry = { uid: userUID, email: ownerEmail, name: ownerName, photoUrl: ownerPhotoUrl, isOwner: true };
        byUid[userUID] = ownerEntry;
        if (ownerEmail) byEmail[ownerEmail] = ownerEntry;
      }

      setTeamDirectory({ byUid, byEmail });
    } catch (e) {
      console.error('Error loading team directory:', e);
    }
  };

  // OPTIMIZED VERSION: Handle both authenticated and imported applicants
  const loadApplicantData = async () => {
    try {
      console.log("🔍 Loading applicant data for:", applicantId);

      // Prefer provided connectionKey when available
      let connectionKey = propConnectionKey;
      let applicantType;
      let selectedJobIndexData = null;

      // Normalize base applicant id for lookups
      const baseApplicantId = applicantId.split('_copy_')[0].replace(/^imported_/, '');

      if (!connectionKey) {
        // Try to resolve exact connectionKey from employer index for current job first
        try {
          if (selectedJobId) {
            const jobIndexRef = ref(database, `companies/${userUID}/applicant_index/by_job/${selectedJobId}`);
            const jobIndexSnap = await get(jobIndexRef);
            if (jobIndexSnap.exists()) {
              const jobIndex = jobIndexSnap.val();
              const foundKey = Object.keys(jobIndex).find(k => jobIndex[k]?.applicantId === baseApplicantId);
              if (foundKey) {
                connectionKey = foundKey;
                selectedJobIndexData = jobIndex[foundKey] || null;
              }
            }
          }
        } catch (e) {
          console.warn('Index lookup by selectedJobId failed:', e);
        }

        // Fallback: search across all jobs
        if (!connectionKey) {
          try {
            const allIndexRef = ref(database, `companies/${userUID}/applicant_index/by_job`);
            const allIndexSnap = await get(allIndexRef);
            if (allIndexSnap.exists()) {
              const allIndex = allIndexSnap.val();
              outer: for (const applicants of Object.values(allIndex)) {
                for (const [ck, data] of Object.entries(applicants)) {
                  if (data?.applicantId === baseApplicantId) {
                    connectionKey = ck;
                    break outer;
                  }
                }
              }
            }
          } catch (e) {
            console.warn('Global index lookup failed:', e);
          }
        }

        // Final fallback: construct from applicantId (may miss copy suffix)
        if (!connectionKey) {
          if (applicantId.startsWith('temp_') || applicantId.startsWith('imported_temp_')) {
            connectionKey = `${userUID}_imported_${baseApplicantId}`;
            applicantType = 'imported';
          } else {
            connectionKey = `${userUID}_${applicantId}`;
            applicantType = 'authenticated';
          }
        }
      }

      if (!selectedJobIndexData && selectedJobId && connectionKey) {
        try {
          const indexItemRef = ref(database, `companies/${userUID}/applicant_index/by_job/${selectedJobId}/${connectionKey}`);
          const indexItemSnap = await get(indexItemRef);
          selectedJobIndexData = indexItemSnap.exists() ? indexItemSnap.val() : null;
        } catch (e) {
          console.warn('Applicant index fit score lookup failed:', e);
        }
      }
      setApplicantIndexData(selectedJobIndexData || {});

      // Verify the connection exists
      const connectionRef = ref(database, `connections/${connectionKey}`);
      const connectionSnapshot = await get(connectionRef);

      if (!connectionSnapshot.exists()) {
        console.error("❌ Connection not found:", connectionKey);
        throw new Error(`Connection ${connectionKey} not found in database`);
      }

      const connectionData = (await get(ref(database, `connections/${connectionKey}`))).val();
      console.log("✅ Found connection data:", connectionData);
      setApplicantConnectionData(connectionData || {});

      // Store applicant type and connection key for other functions
      // Derive applicantType from connection data when possible
      if (!applicantType) {
        applicantType = connectionData.applicantType || (connectionData.tempProfileId ? 'imported' : 'authenticated');
      }
      setApplicantData(prev => ({ ...prev, applicantType, connectionKey }));

      // Subscribe to contact info for this connection
      try {
        const ciRef = ref(database, `connections/${connectionKey}/contactInfo`);
        onValue(ciRef, (snap) => {
          const ci = snap.val() || null;
          setContactInfo(ci);
          if (ci?.email) {
            setApplicantEmail(ci.email);
          }
        });
      } catch (e) { console.warn('Contact info subscribe failed:', e); }

      // Load applicant's personal data based on type
      let personData = null;
      if (applicantType === 'authenticated') {
        const personRef = ref(database, `people/${applicantId}`);
        const personSnapshot = await get(personRef);
        personData = personSnapshot.val();
        console.log("✅ Loaded authenticated user data:", personData);
      } else {
        // For imported applicants, prefer tempProfileId from connection data (base id for profile reads)
        const tempId = connectionData.tempProfileId || baseApplicantId;

        // Try new structure first (imported_profiles/{employerUID}/{tempId})
        let profileRef = ref(database, `imported_profiles/${userUID}/${tempId}`);
        let profileSnapshot = await get(profileRef);

        // Fallback to old structure
        if (!profileSnapshot.exists()) {
          profileRef = ref(database, `imported_profiles/${tempId}`);
          profileSnapshot = await get(profileRef);
        }

        personData = profileSnapshot.val();
        console.log("✅ Loaded imported profile data:", personData);
      }

      if (personData) {
        // Merge person data with type info
        setApplicantData({ ...personData, applicantType, connectionKey });

        if (connectionData && connectionData.applicantAnalysis) {
          setCandidateAnalysis(normalizeAnalysisPayload(connectionData.applicantAnalysis));
        }
      } else {
        console.error("❌ No person data found for applicant:", applicantId, "type:", applicantType);
      }

      // Fetch applicant's email based on type
      if (applicantType === 'authenticated') {
        fetchApplicantEmail(applicantId);
      } else {
        // For imported applicants, email is stored in profile data
        setApplicantEmail(personData?.email || 'No Email On File');
      }
    } catch (error) {
      console.error('❌ Error loading applicant data (optimized):', error);
      alert(uiText('Failed to load applicant data. Please refresh and try again.'));
    } finally {
      setIsApplicantLoading(false);
    }
  };

  const fetchApplicantEmail = async (applicantId) => {
    try {
      const response = await fetch('https://us-central1-jobseeqr.cloudfunctions.net/getUserEmail', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ uid: applicantId })
      });
      const data = await response.json();
      const email = data.email;
      setApplicantEmail(email || 'No Email On File');
    } catch (error) {
      console.log("Error fetching user email:", error);
      setApplicantEmail('No Email On File');
    }
  };

  const copyEmailToClipboard = () => {
    if (applicantEmail && applicantEmail !== 'No Email On File') {
      navigator.clipboard.writeText(applicantEmail).then(() => {
        const originalEmail = applicantEmail;
        setApplicantEmail('Email copied!');
        setTimeout(() => {
          setApplicantEmail(originalEmail);
        }, 3000);
      });
    }
  };

  // OPTIMIZED VERSION: Handle both authenticated and imported applicants for messaging
  const loadApplicantConversation = () => {
    try {
      // Skip messaging for imported candidates - they don't have messaging functionality
      if (applicantData.applicantType === 'imported') {
        setMessages([]);
        return;
      }

      // 🚀 OPTIMIZED: Generate proper connection key based on applicant type
      const connectionPath = applicantData.connectionKey || `${userUID}_${applicantId}`;

      const messagesDbRef = ref(database, `connections/${connectionPath}/messages`);

      // Listen for real-time message updates
      onValue(messagesDbRef, (snapshot) => {
        const messagesData = snapshot.val();
        if (messagesData) {
          const messagesList = Object.entries(messagesData).map(([key, value]) => ({
            id: key,
            ...value
          })).sort((left, right) => new Date(left.timestamp || 0) - new Date(right.timestamp || 0));
          setMessages(messagesList);

          // Mark messages as viewed
          const connectionRef = ref(database, `connections/${connectionPath}`);
          update(connectionRef, { newMsgEmpl: "false" });

          // Scroll to bottom
          setTimeout(() => {
            if (messagesRef.current) {
              messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
            }
          }, 100);
        } else {
          setMessages([]);
        }
      });

      return () => {
        off(messagesDbRef);
      };
    } catch (error) {
      console.error('Error loading conversation:', error);
    }
  };

  const loadInterviews = async () => {
    try {
      setLoadingInterviews(true);
      const interviewsRef = ref(database, `companies/${userUID}/interviews/${applicantId}`);
      const snapshot = await get(interviewsRef);

      if (snapshot.exists()) {
        const interviewsData = snapshot.val();
        const interviewsList = Object.entries(interviewsData).map(([key, value]) => ({
          id: key,
          ...value
        }));

        // Sort interviews: most recent first, completed/cancelled at bottom
        const sortedInterviews = interviewsList.sort((a, b) => {
          const now = new Date();
          const aDate = new Date(`${a.date}T${a.time}`);
          const bDate = new Date(`${b.date}T${b.time}`);

          // Check if interviews are completed/cancelled
          const aIsCompleted = a.status === 'completed' || a.status === 'cancelled' || (a.status !== 'cancelled' && aDate < now);
          const bIsCompleted = b.status === 'completed' || b.status === 'cancelled' || (b.status !== 'cancelled' && bDate < now);

          // If one is completed and the other isn't, put completed at bottom
          if (aIsCompleted && !bIsCompleted) return 1;
          if (!aIsCompleted && bIsCompleted) return -1;

          // If both are completed or both are active, sort by date (most recent first)
          return aDate - bDate;
        });

        setInterviews(sortedInterviews);
      } else {
        setInterviews([]);
      }
    } catch (error) {
      console.error('Error loading interviews:', error);
      setInterviews([]);
    } finally {
      setLoadingInterviews(false);
    }
  };

  const loadCalendarConnections = async () => {
    try {
      const calendarConnectionsRef = ref(database, `companies/${userUID}/calendarIntegrations`);
      const snapshot = await get(calendarConnectionsRef);

      if (snapshot.exists()) {
        const calendarConnections = snapshot.val();
        const calendarConnectionsArray = Object.values(calendarConnections);
        setCalendarConnections(calendarConnectionsArray);
      } else {
        setCalendarConnections([]);
      }
    } catch (error) {
      console.error('Error loading calendar connections:', error);
      setCalendarConnections([]);
    }
  };

  const loadCompanyData = async () => {
    try {
      const companyRef = ref(database, `companies/${userUID}`);
      const snapshot = await get(companyRef);

      if (snapshot.exists()) {
        const companyData = snapshot.val();
        setCompanyName(companyData.companyName || 'Our Company');
      } else {
        setCompanyName('Our Company');
      }
    } catch (error) {
      console.error('Error loading company data:', error);
      setCompanyName('Our Company');
    }
  };

  const loadBuddyInfo = async () => {
    try {
      setLoadingBuddyInfo(true);

      // Determine connection key based on applicant type
      let connectionKey;
      if (applicantId.startsWith('temp_')) {
        connectionKey = `${userUID}_imported_${applicantId}`;
      } else {
        connectionKey = `${userUID}_${applicantId}`;
      }

      const buddyRef = ref(database, `connections/${connectionKey}/buddy`);
      const snapshot = await get(buddyRef);

      if (snapshot.exists()) {
        setBuddyInfo(snapshot.val());
      } else {
        setBuddyInfo(null);
      }
    } catch (error) {
      console.error('Error loading buddy info:', error);
      setBuddyInfo(null);
    } finally {
      setLoadingBuddyInfo(false);
    }
  };

  const loadFirstWeekChecklist = async () => {
    try {
      setLoadingChecklist(true);

      // Determine connection key based on applicant type
      let connectionKey;
      if (applicantId.startsWith('temp_')) {
        connectionKey = `${userUID}_imported_${applicantId}`;
      } else {
        connectionKey = `${userUID}_${applicantId}`;
      }

      const checklistRef = ref(database, `connections/${connectionKey}/firstWeekChecklist`);
      const snapshot = await get(checklistRef);

      if (snapshot.exists()) {
        const checklistData = snapshot.val();
        setFirstWeekChecklist(checklistData);

        // Load progress data
        const progressRef = ref(database, `connections/${connectionKey}/checklistProgress`);
        const progressSnapshot = await get(progressRef);

        if (progressSnapshot.exists()) {
          setChecklistProgress(progressSnapshot.val());
        } else {
          // Initialize empty progress
          const initialProgress = {};
          Object.keys(checklistData.checklist || {}).forEach(day => {
            initialProgress[day] = {};
            checklistData.checklist[day].forEach((task, index) => {
              initialProgress[day][index] = false;
            });
          });
          setChecklistProgress(initialProgress);
        }
      } else {
        setFirstWeekChecklist(null);
        setChecklistProgress({});
      }
    } catch (error) {
      console.error('Error loading first week checklist:', error);
      setFirstWeekChecklist(null);
      setChecklistProgress({});
    } finally {
      setLoadingChecklist(false);
    }
  };


  const loadStarredStatus = async () => {
    try {
      const connectionKey = applicantData.connectionKey;
      if (!connectionKey) return;

      // Read starred status from connection
      const starredRef = ref(database, `connections/${connectionKey}/starred`);
      const snapshot = await get(starredRef);

      if (snapshot.exists()) {
        setIsStarred(snapshot.val() === true);
      } else {
        setIsStarred(false);
      }
    } catch (error) {
      console.error('Error loading starred status:', error);
      setIsStarred(false);
    }
  };

  const toggleStarred = async () => {
    if (togglingStarred || !applicantData?.connectionKey) return;

    setTogglingStarred(true);
    const newStarredStatus = !isStarred;

    try {
      const connectionKey = applicantData.connectionKey;
      console.log(`⭐ Toggling starred for ${applicantData.name}: ${isStarred} -> ${newStarredStatus}`);
      console.log(`🔑 Connection key: ${connectionKey}`);
      console.log(`📋 Selected job ID: ${selectedJobId}`);

      // Update optimistically in UI
      setIsStarred(newStarredStatus);

      // Save to connections (this will trigger cloud function)
      const connectionStarredRef = ref(database, `connections/${connectionKey}/starred`);
      await set(connectionStarredRef, newStarredStatus);
      console.log(`✅ Updated connections/${connectionKey}/starred = ${newStarredStatus}`);

      // Save to employer index (manual update for immediate UI refresh)
      if (selectedJobId) {
        const indexRef = ref(database, `companies/${userUID}/applicant_index/by_job/${selectedJobId}/${connectionKey}/starred`);
        await set(indexRef, newStarredStatus);
        console.log(`✅ Updated index for job ${selectedJobId}: starred = ${newStarredStatus}`);
      } else {
        // If we don't have selectedJobId, search for it in the index
        console.log(`🔍 No selectedJobId provided, searching across all jobs...`);
        const allJobsIndexRef = ref(database, `companies/${userUID}/applicant_index/by_job`);
        const allJobsSnapshot = await get(allJobsIndexRef);

        if (allJobsSnapshot.exists()) {
          const allJobs = allJobsSnapshot.val();
          let foundJob = false;
          // Find which job this applicant belongs to
          for (const [jobId, applicants] of Object.entries(allJobs)) {
            if (applicants && applicants[connectionKey]) {
              const indexRef = ref(database, `companies/${userUID}/applicant_index/by_job/${jobId}/${connectionKey}/starred`);
              await set(indexRef, newStarredStatus);
              console.log(`✅ Updated index for job ${jobId}: starred = ${newStarredStatus}`);
              foundJob = true;
              break;
            }
          }
          if (!foundJob) {
            console.warn(`⚠️ Could not find applicant in any job index`);
          }
        }
      }

      console.log(`✅ Applicant ${newStarredStatus ? 'starred' : 'unstarred'} successfully`);
    } catch (error) {
      console.error('❌ Error toggling starred status:', error);
      // Revert optimistic update on error
      setIsStarred(!newStarredStatus);
      alert(uiText('Failed to update starred status. Please try again.'));
    } finally {
      setTogglingStarred(false);
    }
  };


  const handleRescheduleInterview = (interview, workflowStep = null) => {
    setSelectedInterviewForReschedule(interview);
    setSelectedInterviewWorkflowStep(workflowStep);
    setShowInterviewScheduler(true);
  };

  const buildApplicantAnalyticsPayload = () => {
    const parsedConnection = applicantData?.connectionKey ? parseConnectionKey(applicantData.connectionKey) : null;
    const resolvedApplicantId = parsedConnection?.applicantId || applicantId;
    const resolvedApplicantType = applicantData?.applicantType || parsedConnection?.applicantType || (String(resolvedApplicantId).startsWith('temp_') ? 'imported' : 'authenticated');

    return {
      subject: {
        applicantId: resolvedApplicantId,
        candidateId: applicantData?.candidateId || resolvedApplicantId,
        connectionKey: applicantData?.connectionKey || propConnectionKey || '',
        applicantType: resolvedApplicantType
      },
      job: {
        jobId: selectedJobId || applicantData?.jobId || '',
        roleId: applicantData?.roleId || selectedJobId || applicantData?.jobId || '',
        jobTitle: applicantData?.jobTitle || applicantData?.jobAppliedFor || selectedJobDescription?.title || '',
        department: applicantData?.department || selectedJobDescription?.department || '',
        location: applicantData?.location || selectedJobDescription?.companyLocation || selectedJobDescription?.location || ''
      },
      context: buildActorAnalyticsContext({
        actorUserId: currentUser?.uid || '',
        actorUserEmail: currentUser?.email || '',
        ownerRecruiterId: applicantData?.ownerRecruiterId || '',
        ownerRecruiterEmail: applicantData?.ownerRecruiterEmail || '',
        primarySource: applicantData?.primarySource || '',
        sourceCategory: applicantData?.sourceCategory || '',
        sourceTags: applicantData?.sourceTags || []
      })
    };
  };

  const handleCancelInterview = async (interview) => {
    const messageLanguage = getUiLocale();
    if (!window.confirm(`Are you sure you want to cancel the interview "${interview.title}"?`)) {
      return;
    }

    try {
      const cancelledAtIso = new Date().toISOString();
      // Immediately update the local state to reflect the cancellation
      setInterviews(currentInterviews =>
        currentInterviews.map(int =>
          int.id === interview.id
            ? { ...int, status: 'cancelled', cancelledAt: cancelledAtIso }
            : int
        )
      );

      // Update interview status to cancelled in Firebase
      const interviewRef = ref(database, `companies/${userUID}/interviews/${applicantId}/${interview.id}`);
      await update(interviewRef, {
        status: 'cancelled',
        cancelledAt: cancelledAtIso
      });
      const analyticsPayload = buildApplicantAnalyticsPayload();
      emitAtsAnalyticsEvent({
        company_uid: userUID,
        event_type: 'interview_cancelled',
        occurred_at: cancelledAtIso,
        subject: analyticsPayload.subject,
        job: analyticsPayload.job,
        context: {
          interviewId: interview.id,
          dateTime: interview.date && interview.time ? `${interview.date}T${interview.time}` : interview.dateTime || '',
          status: 'cancelled',
          title: interview.title || '',
          ...analyticsPayload.context
        }
      });

      // Cancel the actual calendar event if calendar is connected (Google or Outlook)
      try {
        const calendarConnection = calendarConnections.find(conn =>
          conn.provider === 'google' || conn.provider === 'outlook'
        );
        const calendarEventId = interview.meetingId || interview.calendarEventId;

        if (calendarConnection && calendarEventId) {
          const calendarResponse = await fetch(`${BACKEND_URL}/cancel-interview`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              calendar_event_id: calendarEventId,
              user_id: userUID,
              token_data: {
                provider: calendarConnection.provider,
                access_token: calendarConnection.access_token,
                refresh_token: calendarConnection.refresh_token,
                token_uri: calendarConnection.token_uri,
                client_id: calendarConnection.client_id,
                client_secret: calendarConnection.client_secret,
                scopes: calendarConnection.scopes
              }
            })
          });

          if (calendarResponse.ok) {
            console.log(`✅ ${calendarConnection.provider === 'google' ? 'Google Calendar' : 'Outlook'} event cancelled successfully`);
          } else {
            console.warn(`⚠️ Could not cancel ${calendarConnection.provider === 'google' ? 'Google Calendar' : 'Outlook'} event`);
          }
        } else {
          console.log('ℹ️ No calendar connection or event ID found, skipping calendar cancellation');
        }
      } catch (error) {
        console.warn('❌ Error cancelling calendar event:', error);
      }

      // Cancel the Cloud Scheduler reminder if it exists
      try {
        const response = await fetch(`${BACKEND_URL}/cancel-interview-reminder/${interview.id}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        });

        if (response.ok) {
          console.log('✅ Interview reminder cancelled successfully');
        } else {
          console.warn('⚠️ Could not cancel interview reminder');
        }
      } catch (error) {
        console.warn('❌ Error cancelling interview reminder:', error);
      }

      // Reload interviews after a short delay to ensure Firebase has updated
      setTimeout(() => {
        loadInterviews();
      }, 500);

      // Send cancellation message to the candidate via Firebase messaging
      const messagesRef = ref(database, `connections/${userUID}_${applicantId}/messages`);
      await push(messagesRef, {
        message: buildInterviewCancellationMessage(interview, messageLanguage),
        user: userUID,
        timestamp: new Date().toISOString(),
        type: 'interview_cancellation'
      });

      console.log('✅ Chat cancellation message sent to candidate');

      // Send cancellation email to the candidate
      try {
        // Get candidate email
        const candidateEmail = applicantData.email || applicantEmail;

        if (candidateEmail) {
          // Get company and job information
          const actualCompanyName = companyName || selectedJobDescription?.companyName || 'Our Company';
          const jobTitle = applicantData.jobTitle || selectedJobDescription?.title || 'Position';

          // Format interview date and time for email
          const interviewDate = new Date(`${interview.date}T${interview.time}`);
          const formattedDate = interviewDate.toLocaleDateString(messageLanguage, {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          });
          const formattedTime = interviewDate.toLocaleTimeString(messageLanguage, {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          });

          // Send cancellation email via backend
          const emailResponse = await fetch(`${BACKEND_URL}/send-interview-cancellation-email`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              companyUID: userUID,
              language: messageLanguage,
              applicant_email: candidateEmail,
              applicant_name: applicantData.name || 'Candidate',
              company_name: actualCompanyName,
              job_title: jobTitle,
              interview_title: interview.title,
              interview_date: formattedDate,
              interview_time: formattedTime,
              interviewer_name: interview.interviewer || 'Our Team'
            })
          });

          if (emailResponse.ok) {
            console.log('✅ Cancellation email sent successfully');
          } else {
            console.warn('⚠️ Failed to send cancellation email');
          }
        } else {
          console.warn('⚠️ No candidate email found, skipping email notification');
        }
      } catch (emailError) {
        console.error('❌ Error sending cancellation email:', emailError);
      }

    } catch (error) {
      console.error('Error cancelling interview:', error);
      alert(uiText('Error cancelling interview. Please try again.'));

      // Reload interviews on error to ensure UI is in sync
      loadInterviews();
    }
  };

  const handleInterviewScheduleSuccess = (data) => {
    setShowInterviewScheduler(false);
    setSelectedInterviewForReschedule(null);
    setSelectedInterviewWorkflowStep(null);
    // Reload interviews to show updated data
    loadInterviews();
  };

  const formatInterviewDateTime = (dateString, timeString) => {
    try {
      const date = new Date(`${dateString}T${timeString}`);
      return date.toLocaleDateString(getUiLocale(), {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return uiText('{{date}} at {{time}}', { date: dateString, time: timeString });
    }
  };

  const getInterviewStatus = (interview) => {
    if (interview.status === 'cancelled') return { text: 'Cancelled', color: '#dc3545', icon: 'fa-ban' };
    if (interview.status === 'completed') return { text: 'Completed', color: '#28a745', icon: 'fa-check-circle' };

    const now = new Date();
    const interviewDate = new Date(`${interview.date}T${interview.time}`);

    if (interviewDate < now) {
      return { text: 'Completed', color: '#28a745', icon: 'fa-check-circle' };
    } else {
      return { text: 'Scheduled', color: '#568ded', icon: 'fa-calendar-check' };
    }
  };

  const handleAnalyze = async () => {
    const resumeLink = resolveCandidateCvLink();
    const jobDescription = resolveJobDescriptionText();

    if (!resumeLink || resumeLink === "") {
      alert(uiText("This applicant hasn't uploaded their resume yet."));
      return;
    }

    if (!jobDescription) {
      alert(uiText("Job description not available for analysis."));
      return;
    }

    setIsAnalyzing(true);

    try {
      const response = await fetch(`${BACKEND_URL}/candidate-analysis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          resumePath: resumeLink,
          jobDescription,
          language: i18n.language || (isRTL ? 'ar' : 'en'),
          companyUid: userUID,
          jobId: selectedJobId,
          applicantId,
          connectionKey: applicantData.connectionKey || propConnectionKey || ''
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Candidate analysis failed');
      }
      const formattedAnalysis = normalizeAnalysisPayload(data.analysis);
      setCandidateAnalysis(formattedAnalysis);
      setShowAnalysis(true);
      await saveApplicantAnalysis(formattedAnalysis, applicantId);

    } catch (error) {
      console.error('Error:', error);
      alert(uiText('Error generating analysis. Please try again.'));
    } finally {
      setIsAnalyzing(false);
    }
  };

  const saveApplicantAnalysis = async (analysis, applicantId) => {
    try {
      const connectionKey = applicantData.connectionKey || `${userUID}_${applicantId}`;
      const applicantRef = ref(database, `connections/${connectionKey}`);
      await update(applicantRef, { applicantAnalysis: analysis });
    } catch (error) {
      console.error("Error saving analysis to Firebase:", error);
    }
  };

  const handleRatingSelect = async (rating) => {
    setSelectedRating(rating);
    if (tutorialMode) {
      const tutorialUid = currentUser?.uid || 'tutorial_current_user';
      setTeamRatings((previous) => ({
        ...previous,
        [tutorialUid]: {
          ...(previous[tutorialUid] || {}),
          rating,
          userEmail: actualUserEmail || currentUserEmailGlobal || currentUser?.email || 'You',
          updatedAt: Date.now()
        }
      }));
      return;
    }
    try {
      // Get the correct connection key based on applicant type
      const connectionKey = applicantData.connectionKey || `${userUID}_${applicantId}`;
      // Save per-user rating
      if (currentUser?.uid) {
        const scoreRef = ref(database, `connections/${connectionKey}/scores/${currentUser.uid}`);
        await set(scoreRef, {
          rating,
          userEmail: actualUserEmail || currentUserEmailGlobal || currentUser.email || '',
          updatedAt: Date.now()
        });
      }
      // Legacy single score for backward compatibility
      const ratingRef = ref(database, `connections/${connectionKey}/score`);
      await set(ratingRef, rating);
    } catch (error) {
      console.error('Error saving rating:', error);
    }
  };

  const handleAddNote = async (noteInput = {}) => {
    const options = noteInput && typeof noteInput === 'object' ? noteInput : {};
    const content = String(options.content ?? newNoteContent).trim();
    if (!content) return false;
    const priority = ['important', 'urgent'].includes(options.priority) ? options.priority : 'normal';
    const tags = [...new Set((Array.isArray(options.tags) ? options.tags : [])
      .map((tag) => String(tag || '').trim().replace(/^#+/, '').slice(0, 30))
      .filter(Boolean))].slice(0, 6);
    const mentions = (Array.isArray(options.mentions) ? options.mentions : [])
      .filter((mention) => mention && mention.uid)
      .slice(0, 12)
      .map((mention) => ({
        uid: String(mention.uid),
        email: String(mention.email || ''),
        name: String(mention.name || ''),
      }));
    const parentId = String(options.parentId || '').trim();
    const requestedThreadId = String(options.threadId || parentId || '').trim();

    if (tutorialMode) {
      const noteId = `tutorial_note_${Date.now()}`;
      setTeamNotes((previous) => [
        ...previous,
        {
          id: noteId,
          content,
          user: currentUser?.email || 'You',
          uid: currentUser?.uid || 'tutorial_current_user',
          timestamp: Date.now(),
          priority,
          tags,
          mentions,
          threadId: requestedThreadId || noteId,
          ...(parentId ? { parentId } : {})
        }
      ]);
      if (!parentId) {
        setNewNoteContent('');
        setShowAddNote(false);
      }
      return true;
    }

    // Ensure we have the actual user's email before saving
    if (!actualUserEmail) {
      await getActualUserEmail();
    }

    try {
      // Get the correct connection key based on applicant type
      const connectionKey = applicantData.connectionKey || `${userUID}_${applicantId}`;
      const notesRef = ref(database, `connections/${connectionKey}/notes`);
      const newNoteRef = push(notesRef);

      const userEmailToUse = actualUserEmail || currentUserEmailGlobal || currentUser?.email || 'Unknown User';

      await set(newNoteRef, {
        content,
        user: userEmailToUse,
        uid: currentUser?.uid || '',
        timestamp: Date.now(),
        priority,
        tags,
        mentions,
        threadId: requestedThreadId || newNoteRef.key,
        ...(parentId ? { parentId } : {})
      });

      // Mentions use the same per-user notification feed as the rest of the
      // employer app, while the structured mention list stays on the note.
      const mentionedColleagues = mentions.filter((mention) => mention.uid !== currentUser?.uid);
      if (mentionedColleagues.length) {
        try {
          const authorName = userEmailToUse.split('@')[0] || 'A teammate';
          const candidateName = applicantData.username || applicantData.name || 'a candidate';
          const jobId = selectedJobId || applicantConnectionData.jobId || applicantConnectionData.postId || '';
          const jobTitle = applicantConnectionData.jobTitle || applicantConnectionData.jobOpening || applicantConnectionData.position || applicantData.jobTitle || '';
          await Promise.all(mentionedColleagues.map(async (mention) => {
            const notificationRef = push(ref(database, `companies/${userUID}/user_notifications/${mention.uid}`));
            await set(notificationRef, {
              id: notificationRef.key,
              type: 'applicant_note_mention',
              ...systemMessage('applicant_note_mention', { author: authorName, candidate: candidateName }),
              title: 'You were mentioned in a candidate note',
              message: `${authorName} mentioned you in a note about ${candidateName}.`,
              text: `${authorName} mentioned you in a note about ${candidateName}.`,
              timestamp: Date.now(),
              applicantId,
              connectionKey,
              noteId: newNoteRef.key,
              jobId,
              jobTitle,
            });
          }));

          const token = await currentUser?.getIdToken();
          if (token && BACKEND_URL) {
            await Promise.all(mentionedColleagues.map((mention) => fetch(`${BACKEND_URL}/send-applicant-note-mention-email`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({
                companyId: userUID,
                recipientUid: mention.uid,
                applicantId,
                connectionKey,
                candidateName,
                jobId,
                jobTitle,
                authorName,
              }),
            }).then(async (response) => {
              if (!response.ok) {
                const payload = await response.json().catch(() => ({}));
                throw new Error(payload.detail || payload.error || 'Mention email failed');
              }
            })));
          }
        } catch (mentionError) {
          // A notification failure must not make a successfully saved note look
          // unsaved, otherwise retrying would create a duplicate discussion.
          console.error('Error notifying mentioned colleagues:', mentionError);
        }
      }

      if (!parentId) {
        setNewNoteContent('');
        setShowAddNote(false);
      }
      return true;
    } catch (error) {
      console.error('Error adding note:', error);
      return false;
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) {
      alert(uiText("Message cannot be empty!"));
      return;
    }

    if (tutorialMode) {
      setMessages((previous) => [
        ...previous,
        {
          id: `tutorial_message_${Date.now()}`,
          message: newMessage.trim(),
          timestamp: Date.now(),
          user: userUID,
          sender: 'employer'
        }
      ]);
      setNewMessage('');
      return;
    }

    try {
      // Use the correct connection key based on applicant type
      const connectionPath = applicantData.connectionKey || `${userUID}_${applicantId}`;
      const messagesRef = ref(database, `connections/${connectionPath}/messages`);
      const newMessageRef = push(messagesRef);

      await set(newMessageRef, {
        message: newMessage,
        timestamp: Date.now(),
        user: userUID
      });

      // Update connection to mark new message for job seeker and track activity
      const connectionRef = ref(database, `connections/${connectionPath}`);
      await update(connectionRef, {
        newMsgJS: "true",
        lastUpdated: Date.now() // Track last activity without modifying createdAt
      });

      setNewMessage('');
    } catch (error) {
      console.error("Error sending message:", error);
      alert(uiText("Failed to send message."));
    }
  };

  const openRejectModal = () => setShowRejectModal(true);
  const closeRejectModal = () => setShowRejectModal(false);

  // Send a polite rejection message (for authenticated users), then remove connection
  const handleRejectAndNotify = async () => {
    if (!applicantData.connectionKey) return;
    setRejectInProgress(true);
    try {
      const isImported = applicantData.applicantType === 'imported';
      const connectionKey = applicantData.connectionKey;
      const analyticsPayload = buildApplicantAnalyticsPayload();
      const rejectedAtIso = new Date().toISOString();

      if (!isImported) {
        // Platform message to candidate (authenticated users only)
        try {
          const messagesDbRef = ref(database, `connections/${connectionKey}/messages`);
          const newMessageRef = push(messagesDbRef);
          const orgName = companyName || 'our team';
          const candidateName = applicantData.username || applicantData.name || 'there';
          const messageText = `Hello ${candidateName.split(' ')[0]},\n\nThank you for your interest in ${orgName}. After careful review, we will not be moving forward at this time.\n\nWe appreciate the time you took to apply and wish you success in your job search.\n\nBest regards,\n${orgName}`;
          await set(newMessageRef, {
            message: messageText,
            timestamp: new Date().toISOString(),
            user: userUID
          });
          await update(ref(database, `connections/${connectionKey}`), { newMsgJS: "true" });
        } catch (e) {
          console.warn('Failed to send rejection platform message:', e);
        }
      }
      else {
        // For imported candidates, if we have an email, open email client with a polite template
        if (applicantEmail && applicantEmail !== 'No Email On File') {
          try {
            const orgName = companyName || 'our team';
            const subject = encodeURIComponent(`Update on your application at ${orgName}`);
            const candidateName = applicantData.username || applicantData.name || '';
            const greeting = candidateName ? `Hello ${candidateName.split(' ')[0]},` : 'Hello,';
            const body = encodeURIComponent(`${greeting}\n\nThank you for your interest in ${orgName}. After careful review, we will not be moving forward at this time.\n\nWe appreciate the time you took to apply and wish you success in your job search.\n\nBest regards,\n${orgName}`);
            window.open(`mailto:${applicantEmail}?subject=${subject}&body=${body}`, '_blank');
          } catch (e) {
            console.warn('Failed to open mail client for rejection email:', e);
          }
        }
      }

      await emitAtsAnalyticsEvent({
        company_uid: userUID,
        event_type: 'candidate_rejected',
        occurred_at: rejectedAtIso,
        subject: {
          ...analyticsPayload.subject,
          outcomeState: 'rejected'
        },
        job: analyticsPayload.job,
        context: {
          originAction: 'applicant_details_reject',
          reason: 'reject_and_notify',
          ...analyticsPayload.context
        }
      });
      await deleteApplicantConnection(applicantId);
      if (onApplicantRemoved) onApplicantRemoved(applicantId);
      onClose();
    } catch (e) {
      console.error('Reject+Notify failed:', e);
      alert(uiText('Failed to send rejection or remove candidate.'));
    } finally {
      setRejectInProgress(false);
      closeRejectModal();
    }
  };

  // Remove candidate from job without sending any message/email
  const handleRemoveOnly = async () => {
    if (!applicantData.connectionKey) return;
    setRejectInProgress(true);
    try {
      const analyticsPayload = buildApplicantAnalyticsPayload();
      await emitAtsAnalyticsEvent({
        company_uid: userUID,
        event_type: 'candidate_removed_from_job',
        occurred_at: new Date().toISOString(),
        subject: {
          ...analyticsPayload.subject,
          outcomeState: 'removed'
        },
        job: analyticsPayload.job,
        context: {
          originAction: 'applicant_details_remove',
          ...analyticsPayload.context
        }
      });
      await deleteApplicantConnection(applicantId);
      if (onApplicantRemoved) onApplicantRemoved(applicantId);
      onClose();
    } catch (e) {
      console.error('Remove-only failed:', e);
      alert(uiText('Failed to remove candidate.'));
    } finally {
      setRejectInProgress(false);
      closeRejectModal();
    }
  };

  const deleteApplicantConnection = async (applicantId) => {
    // 🚀 FIX: Use the connection key that was already determined in loadApplicantData
    const connectionKey = applicantData.connectionKey;

    console.log('🗑️ deleteApplicantConnection called with:', {
      applicantId,
      connectionKey,
      applicantData: {
        applicantType: applicantData.applicantType,
        name: applicantData.name,
        email: applicantData.email
      }
    });

    if (!connectionKey) {
      console.error('❌ No connection key found in applicant data');
      console.error('Available applicant data keys:', Object.keys(applicantData));
      throw new Error('Connection key not found. Please refresh and try again.');
    }

    console.log('🗑️ Proceeding to delete connection with key:', connectionKey);

    // Use optimized removal that cleans up all indexes
    await removeConnectionOptimized(connectionKey);

    console.log('✅ Successfully deleted connection:', connectionKey);
  };

  const toggleSection = (sectionId) => {
    if (sectionId.startsWith('assessment-')) {
      // Handle assessment sections with React state
      setOpenAssessmentSections(prev => ({
        ...prev,
        [sectionId]: !prev[sectionId]
      }));
    } else if (sectionId === 'candidate-analysis-sec') {
      setShowAnalysis(prev => !prev);
    } else if (sectionId === 'scorecardContent') {
      const newShowScorecard = !showScorecard;
      setShowScorecard(newShowScorecard);
    } else if (sectionId === 'interviewContent') {
      const newShowInterviews = !showInterviews;
      setShowInterviews(newShowInterviews);
    } else if (sectionId === 'firstWeekChecklistContent') {
      setCollapsedSections(prev => ({
        ...prev,
        firstWeekChecklistContent: !prev.firstWeekChecklistContent
      }));
    } else if (sectionId === 'applicationResponsesContent') {
      setCollapsedSections(prev => ({
        ...prev,
        applicationResponsesContent: !prev.applicationResponsesContent
      }));
    } else {
      // Handle regular sections with DOM manipulation
      const content = document.getElementById(sectionId);
      const icon = document.getElementById(sectionId.replace('Content', 'Icon'));

      if (content && icon) {
        if (content.style.display === 'none') {
          content.style.display = 'block';
          icon.style.transform = 'rotate(90deg)';
        } else {
          content.style.display = 'none';
          icon.style.transform = 'rotate(0deg)';
        }
      }
    }
  };

  const toggleChecklistTask = async (day, taskIndex) => {
    try {
      // Determine connection key based on applicant type
      let connectionKey;
      if (applicantId.startsWith('temp_')) {
        connectionKey = `${userUID}_imported_${applicantId}`;
      } else {
        connectionKey = `${userUID}_${applicantId}`;
      }

      const newProgress = {
        ...checklistProgress,
        [day]: {
          ...checklistProgress[day],
          [taskIndex]: !checklistProgress[day]?.[taskIndex]
        }
      };

      setChecklistProgress(newProgress);

      // Save to Firebase
      const progressRef = ref(database, `connections/${connectionKey}/checklistProgress`);
      await set(progressRef, newProgress);

      console.log('✅ Checklist progress updated');
    } catch (error) {
      console.error('❌ Error updating checklist progress:', error);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const renderAssessmentContent = (substageId) => {
    const data = assessmentData[substageId];

    if (!data) {
      return (
        <p style={{ opacity: '0.7', fontSize: '16px' }}>
          <i className="fa-solid fa-info-circle" style={{ color: '#568ded', marginRight: '5px', fontSize: '20px' }}></i>
          {t('details.assessment.noData')}
        </p>
      );
    }

    const { response, evaluation, questions, assessment, liveReview, assessmentId } = data;
    const rubricOutcome = evaluation?.result?.rubric_outcome;
    const rubricOutcomeStatus = rubricOutcome?.status;
    const effectiveResponseStatus = evaluation?.result
      ? (rubricOutcomeStatus === 'manual_review' ? 'manual_review' : 'evaluated')
      : response?.status;
    const spokenLanguageEvidence = (evaluation?.spokenLanguageEvidence || [])
      .map((item) => item?.evidence)
      .filter(Boolean);
    const detectedSpokenLanguages = [...new Set(
      spokenLanguageEvidence.flatMap((evidence) => evidence.detected_languages || [])
    )];
    const codeSwitchingDetected = spokenLanguageEvidence.some(
      (evidence) => evidence.code_switching_detected
    );

    if (!response) {
      return (
        <p style={{ opacity: '0.7', fontSize: '16px' }}>
          <i className="fa-solid fa-info-circle" style={{ color: '#568ded', marginRight: '5px', fontSize: '20px' }}></i>
          {t('details.assessment.notCompleted')}
        </p>
      );
    }

    return (
      <div style={{
        fontSize: '16px',
        lineHeight: '1.7',
        whiteSpace: 'pre-wrap',
        wordWrap: 'break-word',
        fontFamily: 'Montserrat, sans-serif',
        color: "var(--app-neutral-ink, #333)",
        padding: '0',
        margin: '0'
      }}>
        {/* Assessment Overview */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '18px', fontWeight: "var(--app-weight-strong, bold)", color: "var(--app-neutral-ink, #333)", marginBottom: '8px' }}>
            {assessment.title}
          </div>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>
            {t('details.assessment.status')}: <span style={{
              color: effectiveResponseStatus === 'evaluated'
                ? 'var(--app-tone-28a745, #28a745)'
                : effectiveResponseStatus === 'manual_review'
                  ? 'var(--app-tone-b7791f, #b7791f)'
                  : 'var(--app-tone-ffc107, #ffc107)'
            }}>
              {effectiveResponseStatus === 'evaluated'
                ? t('details.assessment.evaluated')
                : effectiveResponseStatus === 'manual_review'
                  ? t('details.assessment.manualReview')
                  : t('details.assessment.pending')}
            </span>
          </div>
          {response.submittedAt && (
            <div style={{ fontSize: '14px', color: '#666' }}>
              {t('details.assessment.submitted')}: {new Date(response.submittedAt).toLocaleDateString(dateLocale)}
            </div>
          )}
        </div>

        {assessment?.deliveryType === 'two-way-voice' && liveReview && (
          <div style={{ marginBottom: '25px', padding: '14px', backgroundColor: '#f5f8ff', border: '1px solid #d9e4fb', borderRadius: '8px' }}>
            <div style={{ fontSize: '18px', fontWeight: "var(--app-weight-strong, bold)", marginBottom: '10px' }}>{uiText("Interview recording and transcript")}</div>
            <div style={{ fontSize: '13px', color: '#596579', marginBottom: '12px' }}>{uiText("Recording: ")}{liveReview.recording?.status || uiText("not ready")}{uiText(" · Results are based on the candidate's spoken answers, not video quality.")}</div>
            {!evaluation?.result && Array.isArray(liveReview.transcript) && liveReview.transcript.length > 0 && (
              <div style={{ marginBottom: '12px', padding: '12px', border: '1px solid #d9e4fb', borderRadius: '10px', background: '#fff' }}>
                <div style={{ color: '#23314d', fontSize: '13px', fontWeight: "var(--app-weight-strong, 700)", marginBottom: '8px' }}>
                  {liveReview.evaluationState?.status === 'evaluation_failed'
                    ? uiText("The results need another try.")
                    : uiText("The answers were saved, but the results are not ready.")}
                </div>
                <button
                  type="button"
                  disabled={evaluationRetryState[substageId]?.loading}
                  onClick={() => retryLiveEvaluation(substageId, assessmentId, liveReview.sessionId)}
                  style={{ '--light-control-fill': '#568ded', minHeight: '40px', border: 0, borderRadius: '10px', backgroundColor: '#568ded', color: '#fff', padding: '0 14px', cursor: evaluationRetryState[substageId]?.loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', fontWeight: "var(--app-weight-strong, 800)", opacity: evaluationRetryState[substageId]?.loading ? 0.6 : 1 }}
                  data-light-control={"filled"}>
                  <i className={`fa-solid ${evaluationRetryState[substageId]?.loading ? 'fa-spinner fa-spin' : 'fa-rotate-right'}`} style={{ marginRight: '6px' }}></i>
                  {evaluationRetryState[substageId]?.loading ? uiText("Preparing results…") : uiText("Try again")}
                </button>
                {evaluationRetryState[substageId]?.error && (
                  <div role="alert" style={{ color: 'var(--app-tone-b42318, #b42318)', fontSize: '12px', marginTop: '8px' }}>
                    {evaluationRetryState[substageId].error}
                  </div>
                )}
              </div>
            )}
            {liveReview.recording?.status === 'uploaded' && !privateRecordingUrls[substageId] && (
              <button type="button" onClick={() => loadPrivateRecording(substageId, assessmentId, liveReview.sessionId)} style={{ '--light-control-fill': '#568ded', border: 0, borderRadius: '18px', backgroundColor: '#568ded', color: '#fff', padding: '8px 14px', cursor: 'pointer' }}  data-light-control={"filled"}>
                <i className="fa-solid fa-lock" style={{ marginRight: '6px' }}></i>{uiText("Open private recording")}</button>
            )}
            {privateRecordingUrls[substageId]?.url && (
              <video controls preload="metadata" src={privateRecordingUrls[substageId].url} style={{ width: '100%', maxWidth: '560px', borderRadius: '8px', backgroundColor: '#000' }} />
            )}
            {Array.isArray(liveReview.transcript) && liveReview.transcript.length > 0 && (
              <details style={{ marginTop: '14px' }}>
                <summary style={{ cursor: 'pointer', fontWeight: 600 }}>{uiText("Read transcript")}</summary>
                <div style={{ display: 'grid', gap: '8px', marginTop: '10px' }}>
                  {liveReview.transcript.map((turn, index) => (
                    <div key={turn.id || index} style={{ padding: '8px 10px', background: '#fff', borderRadius: '6px' }}>
                      <strong>{turn.role === 'candidate' ? uiText("Candidate") : uiText("AI interviewer")}:</strong> {turn.text}
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}

        {/* Evaluation Results (if available) */}
        {evaluation && evaluation.result && (
          <div style={{ marginBottom: '25px' }}>
            <div style={{ fontSize: '18px', fontWeight: "var(--app-weight-strong, bold)", color: "var(--app-neutral-ink, #333)", marginBottom: '15px' }}>
              📊 {t('details.assessment.aiResults')}
            </div>

            {/* Rubric outcome / Fit Score */}
            <div style={{ marginBottom: '15px', padding: '12px', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #ddd' }}>
              <div style={{ fontSize: '16px', fontWeight: "var(--app-weight-strong, bold)", marginBottom: '8px' }}>
                {rubricOutcomeStatus === 'void' || rubricOutcomeStatus === 'manual_review'
                  ? t('details.assessment.rubricOutcome')
                  : t('details.assessment.fitScore')}
              </div>
              <div style={{
                fontSize: '28px',
                fontWeight: "var(--app-weight-strong, bold)",
                color: rubricOutcomeStatus === 'void' ? 'var(--app-tone-dc3545, #dc3545)' :
                  rubricOutcomeStatus === 'manual_review' ? 'var(--app-tone-b7791f, #b7791f)' :
                  evaluation.result.fit_score >= 7 ? 'var(--app-tone-28a745, #28a745)' :
                  evaluation.result.fit_score >= 5 ? 'var(--app-tone-ffc107, #ffc107)' : 'var(--app-tone-dc3545, #dc3545)'
              }}>
                {rubricOutcomeStatus === 'void'
                  ? t('details.assessment.void')
                  : rubricOutcomeStatus === 'manual_review'
                    ? t('details.assessment.manualReview')
                    : `${evaluation.result.fit_score}/10`}
              </div>
              {(rubricOutcomeStatus === 'void' || rubricOutcomeStatus === 'manual_review') && rubricOutcome?.reason && (
                <div style={{ marginTop: '6px', color: '#666', fontSize: '14px' }}>
                  {rubricOutcome.reason}
                </div>
              )}
            </div>

            {detectedSpokenLanguages.length > 0 && (
              <div style={{ marginBottom: '15px', padding: '12px', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #ddd' }}>
                <div style={{ fontSize: '16px', fontWeight: "var(--app-weight-strong, bold)", marginBottom: '6px' }}>
                  {t('details.assessment.spokenLanguages')}
                </div>
                <div style={{ fontSize: '15px', color: '#444' }}>
                  {detectedSpokenLanguages.map((code) => code.toUpperCase()).join(', ')}
                  {codeSwitchingDetected && ` · ${t('details.assessment.codeSwitching')}`}
                </div>
              </div>
            )}

            {/* Overall Summary */}
            <div style={{ marginBottom: '15px', padding: '12px', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #ddd' }}>
              <div style={{ fontSize: '16px', fontWeight: "var(--app-weight-strong, bold)", marginBottom: '8px' }}>
                {t('details.assessment.overallSummary')}
              </div>
              <div style={{ fontSize: '15px', lineHeight: '1.6' }}>
                {evaluation.result.overall_summary}
              </div>
            </div>

            {/* Strengths */}
            {evaluation.result.strengths && evaluation.result.strengths.length > 0 && (
              <div style={{ marginBottom: '15px', padding: '12px', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #ddd' }}>
                <div style={{ fontSize: '16px', fontWeight: "var(--app-weight-strong, bold)", marginBottom: '8px', color: 'var(--app-tone-28a745, #28a745)' }}>
                  ✅ {t('details.assessment.strengths')}
                </div>
                {evaluation.result.strengths.map((strength, index) => (
                  <div key={index} style={{
                    fontSize: '15px',
                    lineHeight: '1.6',
                    marginBottom: '6px',
                    paddingLeft: '10px'
                  }}>
                    • {strength}
                  </div>
                ))}
              </div>
            )}

            {/* Areas for Improvement */}
            {evaluation.result.improvement_areas && evaluation.result.improvement_areas.length > 0 && (
              <div style={{ marginBottom: '15px', padding: '12px', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #ddd' }}>
                <div style={{ fontSize: '16px', fontWeight: "var(--app-weight-strong, bold)", marginBottom: '8px', color: 'var(--app-tone-dc3545, #dc3545)' }}>
                  🔍 {t('details.assessment.improvements')}
                </div>
                {evaluation.result.improvement_areas.map((area, index) => (
                  <div key={index} style={{
                    fontSize: '15px',
                    lineHeight: '1.6',
                    marginBottom: '6px',
                    paddingLeft: '10px'
                  }}>
                    • {area}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Question-by-Question Review */}
        <div>
          <div style={{ fontSize: '18px', fontWeight: "var(--app-weight-strong, bold)", color: "var(--app-neutral-ink, #333)", marginBottom: '15px' }}>
            📝 {t('details.assessment.responseReview')}
          </div>

          {questions.map((question, index) => {
            const rawAnswer = response.answers?.[question.id];
            const answer = assessmentAnswerText(rawAnswer, t('details.assessment.noAnswer'));
            const qEvaluation = assessmentQuestionEvaluation(
              evaluation?.result?.question_breakdown,
              question
            );

            return (
              <div key={question.id} style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #ddd' }}>
                <div style={{ fontSize: '16px', fontWeight: "var(--app-weight-strong, bold)", marginBottom: '10px', color: "var(--app-neutral-ink, #333)" }}>
                  {t('details.assessment.questionLabel', { number: index + 1 })}: {question.text}
                </div>

                <div style={{ padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '6px', marginBottom: qEvaluation ? '10px' : '0' }}>
                  <div style={{ fontSize: '14px', fontWeight: "var(--app-weight-strong, bold)", marginBottom: '5px', color: '#666' }}>
                    {t('details.assessment.candidateAnswer')}
                  </div>
                  <div style={{ fontSize: '15px', lineHeight: '1.6' }}>
                    {/* VIDEO RESPONSE DISPLAY */}
                    {question.type === 'record-video' && typeof answer === 'string' && answer.startsWith('http') ? (
                      <div style={{ marginTop: '10px' }}>
                        <div style={{
                          marginBottom: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <i className="fa-solid fa-video" style={{ color: '#568ded', fontSize: '16px' }}></i>
                            <span style={{
                              fontSize: '14px',
                              fontWeight: "var(--app-weight-strong, bold)",
                              color: '#568ded'
                            }}>
                              {t('details.assessment.videoResponse')}
                            </span>
                          </div>
                          <div style={{
                            fontSize: '10px',
                            color: 'var(--app-tone-28a745, #28a745)',
                            backgroundColor: 'rgba(40, 167, 69, 0.1)',
                            padding: '2px 6px',
                            borderRadius: '10px',
                            border: '1px solid rgba(40, 167, 69, 0.3)',
                            fontWeight: "var(--app-weight-strong, bold)"
                          }}>
                            <i className="fa-solid fa-compress-alt" style={{ marginRight: '3px' }}></i>
                            {t('details.assessment.optimized')}
                          </div>
                        </div>

                        {/* Show video player only if no error */}
                        {!videoLoadErrors[`${question.id}_${index}`] ? (
                          <video
                            controls
                            style={{
                              width: '100%',
                              maxWidth: '400px',
                              height: 'auto',
                              borderRadius: '6px',
                              backgroundColor: '#000',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }}
                            preload="metadata"
                            poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='225' viewBox='0 0 400 225'%3E%3Crect width='400' height='225' fill='%23000'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23fff' font-family='Arial' font-size='14'%3EOptimized Video Response%3C/text%3E%3Ctext x='50%25' y='65%25' dominant-baseline='middle' text-anchor='middle' fill='%23aaa' font-family='Arial' font-size='12'%3E480p • Speech-to-Text Enabled%3C/text%3E%3C/svg%3E"
                            onLoadStart={() => {
                              console.log('🎬 Loading optimized video for question:', question.id);
                            }}
                            onCanPlayThrough={() => {
                              console.log('✅ Optimized video ready to play smoothly');
                            }}
                            onError={(e) => {
                              console.error('❌ Video failed to load:', answer);
                              setVideoLoadErrors(prev => ({
                                ...prev,
                                [`${question.id}_${index}`]: true
                              }));
                            }}
                          >
                            <source src={answer} type="video/webm" />
                            <source src={answer} type="video/mp4" />{uiText("Your browser does not support the video tag.")}</video>
                        ) : (
                          /* Show error message if video failed to load */
                          <div style={{
                            padding: '15px',
                            backgroundColor: '#fff3cd',
                            border: '1px solid #ffeaa7',
                            borderRadius: '6px',
                            marginTop: '10px'
                          }}>
                            <div style={{ fontSize: '14px', color: 'var(--app-tone-856404, #856404)', marginBottom: '8px' }}>
                              <i className="fa-solid fa-exclamation-triangle" style={{ marginRight: '5px' }}></i>{uiText("Video could not be loaded")}</div>
                            <div style={{ fontSize: '12px', color: '#6c757d', wordBreak: 'break-all' }}>{uiText("Video URL: ")}{answer}
                            </div>
                            <button
                              onClick={() => {
                                setVideoLoadErrors(prev => ({
                                  ...prev,
                                  [`${question.id}_${index}`]: false
                                }));
                              }}
                              style={{ '--light-control-fill': '#568ded',
                                marginTop: '8px',
                                padding: '6px 12px',
                                backgroundColor: '#568ded',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                fontSize: '12px',
                                cursor: 'pointer'
                              }}
                              data-light-control={"filled"}>
                              <i className="fa-solid fa-refresh" style={{ marginRight: '4px' }}></i>{uiText("Try Again")}</button>
                          </div>
                        )}
                      </div>
                    ) : (
                      /* REGULAR TEXT/OTHER RESPONSE DISPLAY */
                      Array.isArray(answer) ? answer.join(', ') : answer
                    )}
                  </div>
                </div>

                {qEvaluation && (
                  <div style={{ padding: '10px', backgroundColor: '#e8f4f8', borderRadius: '6px' }}>
                    <div style={{ fontSize: '14px', fontWeight: "var(--app-weight-strong, bold)", marginBottom: '5px', color: 'var(--app-tone-0066cc, #0066cc)' }}>
                      {t('details.assessment.aiEvaluation')}:
                    </div>
                    <div style={{ fontSize: '15px', lineHeight: '1.6' }}>
                      {qEvaluation.evaluation}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderAnalysisList = (items, emptyText) => {
    if (!items || items.length === 0) {
      return <p style={{ margin: 0, color: '#6c757d', fontSize: '14px' }}>{emptyText}</p>;
    }

    return (
      <ul style={{ margin: '0', paddingInlineStart: '20px', display: 'grid', gap: '8px' }}>
        {items.map((item, index) => (
          <li key={`${item}-${index}`} style={{ fontSize: '14px', lineHeight: '1.55', color: '#263247' }}>
            {item}
          </li>
        ))}
      </ul>
    );
  };

  const renderAnalysisCard = (title, children, icon, color = '#568ded') => (
    <div style={{
      padding: '14px',
      backgroundColor: '#fff',
      border: '1px solid #e1e7f0',
      borderRadius: '8px'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
        <i className={`fa-solid ${icon}`} style={{ color }}></i>
        <span style={{ fontSize: '14px', fontWeight: "var(--app-weight-strong, 700)", color: 'var(--app-heading, #031128)' }}>{title}</span>
      </div>
      {children}
    </div>
  );

  const renderCandidateAnalysisTab = () => {
    const analysis = normalizeAnalysisPayload(candidateAnalysis);

    if (!analysis) {
      return (
        <p id="placeholder-analysis" style={{ opacity: '0.7', fontSize: '16px', fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat' }}>
          <i className="fa-solid fa-info-circle" style={{ color: '#568ded', marginInlineEnd: '5px', fontSize: '20px' }}></i>
          {t('details.analysisPlaceholder')}
        </p>
      );
    }

    if (analysis.legacyText) {
      return (
        <div id="true-analysis" style={{
          fontSize: '16px', lineHeight: '1.7', whiteSpace: 'pre-wrap',
          wordWrap: 'break-word', fontFamily: 'Montserrat, sans-serif', color: "var(--app-neutral-ink, #333)", padding: '0', margin: '0'
        }}>
          {renderFormattedAnalysis(formatAnalysisText(analysis.legacyText))}
        </div>
      );
    }

    if (activeAnalysisTab === 'interview') {
      return renderAnalysisCard(
        t('details.analysis.interviewQuestions'),
        renderAnalysisList(analysis.interview_questions, t('details.analysis.interviewQuestionsEmpty')),
        'fa-comments',
        '#568ded'
      );
    }

    if (activeAnalysisTab === 'benchmarks') {
      const benchmarks = analysis.benchmarks || {};
      const rows = [
        [t('details.analysis.marketPosition'), benchmarks.market_position],
        [t('details.analysis.roleLevelAlignment'), benchmarks.role_level_alignment],
        [t('details.analysis.experienceBenchmark'), benchmarks.experience_benchmark],
        [t('details.analysis.evidenceQuality'), benchmarks.evidence_quality]
      ].filter(([, value]) => value);

      return (
        <div style={{ display: 'grid', gap: '12px' }}>
          {rows.length > 0 ? rows.map(([label, value]) => (
            <React.Fragment key={label}>
              {renderAnalysisCard(
                label,
                <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6', color: '#263247' }}>{value}</p>,
                'fa-chart-line',
                '#6f42c1'
              )}
            </React.Fragment>
          )) : (
            <p style={{ margin: 0, color: '#6c757d', fontSize: '14px' }}>{t('details.analysis.benchmarksEmpty')}</p>
          )}
          {renderAnalysisCard(
            t('details.analysis.benchmarkNotes'),
            renderAnalysisList(benchmarks.benchmark_notes, t('details.analysis.benchmarkNotesEmpty')),
            'fa-list-check',
            '#6f42c1'
          )}
        </div>
      );
    }

    return (
      <div style={{ display: 'grid', gap: '12px' }}>
        {renderAnalysisCard(
          t('details.analysis.summary'),
          <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6', color: '#263247' }}>
            {analysis.summary || t('details.analysis.summaryEmpty')}
          </p>,
          'fa-align-left',
          '#568ded'
        )}
        {renderAnalysisCard(
          t('details.analysis.strongPoints'),
          renderAnalysisList(analysis.strong_points, t('details.analysis.strongPointsEmpty')),
          'fa-circle-check',
          '#198754'
        )}
        {renderAnalysisCard(
          t('details.analysis.weakPoints'),
          renderAnalysisList(analysis.weak_points, t('details.analysis.weakPointsEmpty')),
          'fa-triangle-exclamation',
          '#d97706'
        )}
      </div>
    );
  };

  // The redesign is the default. Keep the legacy popup available as an explicit rollback.
  const useNewApplicantDetailsDesign = process.env.REACT_APP_USE_LEGACY_APPLICANT_DETAILS !== 'true';

  if (useNewApplicantDetailsDesign) {
    const resumeLink = resolveCandidateCvLink();
    return (
      <>
        <ApplicantDetailsPopupV2
          applicantData={applicantData}
          isLoading={isApplicantLoading}
          applicantConnectionData={applicantConnectionData}
          applicantIndexData={applicantIndexData}
          candidateEvaluation={candidateEvaluation}
          loadingCandidateEvaluation={loadingCandidateEvaluation}
          applicantEmail={applicantEmail}
          contactInfo={contactInfo}
          candidateAnalysis={candidateAnalysis}
          applicationArtifacts={applicationArtifacts}
          workflowStages={workflowStages}
          dynamicSubstages={dynamicSubstages}
          selectedJobDepartment={selectedJobDepartment}
          interviews={interviews}
          loadingInterviews={loadingInterviews}
          assessmentData={assessmentData}
          loadingAssessmentData={loadingAssessmentData}
          teamNotes={teamNotes}
          teamDirectory={teamDirectory}
          messages={messages}
          userUID={userUID}
          newNoteContent={newNoteContent}
          setNewNoteContent={setNewNoteContent}
          newMessage={newMessage}
          setNewMessage={setNewMessage}
          teamMessageBoxRef={teamMessageBoxRef}
          messageBoxRef={messageBoxRef}
          messagesRef={messagesRef}
          onMessageKeyDown={handleKeyDown}
          onAddNote={handleAddNote}
          onSendMessage={handleSendMessage}
          onToggleStarred={toggleStarred}
          isStarred={isStarred}
          togglingStarred={togglingStarred}
          onCopyEmail={copyEmailToClipboard}
          onClose={onClose}
          onOpenResume={() => resumeLink && window.open(resumeLink, '_blank', 'noopener,noreferrer')}
          resumeLink={resumeLink}
          onAnalyze={handleAnalyze}
          isAnalyzing={isAnalyzing}
          onReject={openRejectModal}
          onRemove={handleRemoveOnly}
          onScheduleInterview={(workflowStep) => {
            setSelectedInterviewForReschedule(null);
            setSelectedInterviewWorkflowStep(workflowStep || null);
            setShowInterviewScheduler(true);
          }}
          onRescheduleInterview={handleRescheduleInterview}
          onCancelInterview={handleCancelInterview}
          offerApproval={offerApproval}
          onViewApprovalDetails={onViewApprovalDetails}
          buddyInfo={buddyInfo}
          firstWeekChecklist={firstWeekChecklist}
          checklistProgress={checklistProgress}
          onToggleChecklist={toggleChecklistTask}
          fitScore={getStoredFitScore() ?? normalizePercentScore(applicantData.fitScore)}
          initialTab={initialTab}
          isRTL={isRTL}
        />

        {showInterviewScheduler && (
          <InterviewScheduler
            applicantId={applicantId}
            userUID={userUID}
            onClose={() => {
              setShowInterviewScheduler(false);
              setSelectedInterviewForReschedule(null);
              setSelectedInterviewWorkflowStep(null);
            }}
            onScheduleSuccess={handleInterviewScheduleSuccess}
            jobTitle={applicantData.jobTitle || selectedJobDescription?.title || 'Position'}
            companyName={companyName || 'Our Company'}
            existingInterview={selectedInterviewForReschedule}
            substageTitle={selectedInterviewWorkflowStep?.title || selectedInterviewForReschedule?.title || ''}
            substageData={selectedInterviewWorkflowStep}
          />
        )}

        {showRejectModal && (
          <div className="modal-overlay" style={{ zIndex: 6000 }}>
            <div className="modal-content" style={{ maxWidth: '560px' }}>
              <div className="modal-header">
                <h2>{t('details.rejectCandidate')}</h2>
                <button className="close" onClick={closeRejectModal}>&times;</button>
              </div>
              <div className="form-section" style={{ padding: '24px 32px' }}>
                <p style={{ marginTop: 0, color: 'var(--app-heading, #031128)' }}>{t('details.rejectConfirmMessage')}</p>
              </div>
              <div className="modal-footer">
                <button className="btn-secondary" onClick={closeRejectModal} disabled={rejectInProgress}>{t('workspace.cancel')}</button>
                <button className="btn-primary" onClick={handleRejectAndNotify} disabled={rejectInProgress}>
                  {t('details.sendRejection')}
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="applicant-details-overlay" style={{ display: 'flex', direction: isRTL ? 'rtl' : 'ltr' }}>
      <div className="applicant-details-modal" style={{ position: 'relative' }}>
        <div className="close" onClick={onClose} style={{
          position: 'absolute',
          top: '12px',
          [isRTL ? 'left' : 'right']: '12px',
          cursor: 'pointer'
        }}>&times;</div>
        {/* Content Container */}
        <div style={{ display: 'flex' }}>
          <div style={{ width: '100%' }}>
            {/* Header Section */}
            <div id='applicant-popup-top' style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
                <div style={{ [isRTL ? 'marginRight' : 'marginLeft']: '50px' }}>
                  <p id="applicantName" style={{ fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat', fontWeight: "var(--app-weight-strong, bold)", fontSize: '30px' }}>
                    {applicantData.username || applicantData.name || t('details.applicantName')}
                  </p>

                  <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <i id="li-logo" className="fa-brands fa-linkedin" style={{ color: '#568ded', marginInlineEnd: '5px', fontSize: '24px' }}></i>
                    <a href={applicantData.linkedin || '#'} id="personli" style={{ fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat', fontSize: '16px', color: '#568ded' }} target="_blank" rel="noopener noreferrer">
                      {applicantData.linkedin ? t('details.visitLinkedIn') : t('details.noLinkedIn')}
                    </a>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', width: '100%', marginTop: '5px' }}>
                    <i id="env-logo" className="fa-solid fa-envelope" style={{ color: '#568ded', marginInlineEnd: '5px', fontSize: '24px' }}></i>
                    <button type="button" id="personemail" onClick={copyEmailToClipboard} style={{
                      fontFamily: 'Montserrat', fontSize: '16px', color: '#568ded', cursor: 'pointer',
                      padding: 0, border: 'none', background: 'transparent'
                    }}  data-light-control={"quiet"}>
                      {applicantEmail}
                    </button>
                  </div>
                  {contactInfo?.phone && (
                    <div style={{ display: 'flex', alignItems: 'center', width: '100%', marginTop: '5px' }}>
                      <i className="fa-solid fa-phone" style={{ color: '#568ded', marginInlineEnd: '6px', fontSize: '20px' }}></i>
                      <span style={{ fontFamily: 'Montserrat', fontSize: '15px', color: 'var(--app-heading, #031128)' }}>{contactInfo.phone}</span>
                    </div>
                  )}
                  {offerApproval && (
                    <div style={{
                      marginTop: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '10px 12px',
                      borderRadius: '10px',
                      background: '#fff7e6',
                      border: '1px solid #ffe3b3',
                      flexWrap: 'wrap'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <i className="fa-solid fa-envelope-open-text" style={{ color: 'var(--app-tone-f59e0b, #f59e0b)' }}></i>
                        <span style={{ fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat', fontSize: '13px', fontWeight: 600, color: 'var(--app-tone-8a5b00, #8a5b00)' }}>{uiText("Offer awaiting approval")}</span>
                      </div>
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={() => onViewApprovalDetails && onViewApprovalDetails(offerApproval)}
                        style={{ padding: '6px 12px', fontSize: '12px', fontWeight: 600 }}
                      >{uiText("Review Offer")}</button>
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', [isRTL ? 'marginLeft' : 'marginRight']: '30px', gap: '10px' }}>
                  <button
                    id="starApplicantBtn"
                    onClick={toggleStarred}
                    disabled={togglingStarred}
                    style={{
                      backgroundColor: 'transparent',
                      border: 'none',
                      cursor: togglingStarred ? 'not-allowed' : 'pointer',
                      padding: '8px',
                      fontSize: '24px',
                      color: isStarred ? 'var(--app-tone-ffc107, #ffc107)' : 'var(--app-text, #d0d0d0)',
                      transition: 'color 0.2s ease'
                    }}
                    title={isStarred ? t('details.unstarApplicant') : t('details.starApplicant')}
                    data-light-control={"quiet"}>
                    <i className={`fa-${isStarred ? 'solid' : 'regular'} fa-star`}></i>
                  </button>
                  <button id="viewCVBtn" onClick={() => window.open(resolveCandidateCvLink() || '#', '_blank')} className="btn-primary"
                    style={{ fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat' }}>
                    <i className="fa-solid fa-file-lines" style={{ marginInlineEnd: '7px' }}></i>{t('details.viewResume')}
                  </button>
                  {applicantData.applicantType === 'imported' && (
                    <button onClick={handleRemoveOnly} className="btn-secondary"
                      style={{ fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat' }}>
                      <i className="fa-solid fa-trash" style={{ marginInlineEnd: '7px' }}></i>{t('details.removeFromJob')}
                    </button>
                  )}
                  <button id="rejectCandidateBtn" onClick={openRejectModal} className="btn-primary"
                    style={{ fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat' }}>
                    <i className="fa-solid fa-circle-xmark" style={{ marginInlineEnd: '7px' }}></i>{t('details.reject')}
                  </button>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div style={{ display: 'flex', margin: '20px 50px', justifyContent: 'space-between' }}>
              {/* Left Column - Analysis and Assessments */}
              <div id="toggleContainer" style={{ width: applicantData.applicantType === 'imported' ? '48%' : '48%', display: 'flex', flexDirection: 'column', gap: '20px', height: '67vh', overflowY: 'scroll', overflowX: 'hidden' }}>

                {/* Buddy Information Display */}
                {buddyInfo && (
                  <div style={{
                    backgroundColor: 'var(--app-surface, rgba(86, 141, 237, 0.1))',
                    border: "1px solid var(--app-border-strong, rgba(86, 141, 237, 0.3))",
                    borderRadius: '20px',
                    padding: '15px 20px',
                    marginBottom: '10px'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '8px'
                    }}>
                      <i className="fa-solid fa-user-friends" style={{
                        color: '#568ded',
                        fontSize: '18px',
                        marginRight: '8px'
                      }}></i>
                      <span style={{
                        fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat',
                        fontSize: '16px',
                        fontWeight: "var(--app-weight-strong, bold)",
                        color: '#568ded'
                      }}>
                        {t('details.assignedBuddy')}
                      </span>
                    </div>
                    <div style={{
                      fontFamily: 'Montserrat',
                      fontSize: '15px',
                      color: "var(--app-neutral-ink, #333)",
                      lineHeight: '1.4'
                    }}>
                      <div style={{ marginBottom: '4px' }}>
                        <strong>{buddyInfo.name}</strong> • {buddyInfo.position}
                      </div>
                      <div style={{ fontSize: '14px', color: '#666' }}>
                        📧 {buddyInfo.email}
                        {buddyInfo.phone && (
                          <span> • 📞 {buddyInfo.phone}</span>
                        )}
                      </div>
                      {buddyInfo.assignedAt && (
                        <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                          {t('details.buddy.assignedOn', { date: new Date(buddyInfo.assignedAt).toLocaleDateString(dateLocale) })}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* First Week Checklist Section */}
                {firstWeekChecklist && (
                  <div id="firstWeekChecklistWrapper">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div onClick={() => toggleSection('firstWeekChecklistContent')} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                        <i id="firstWeekChecklistIcon" className={`fa-solid ${collapsedSections.firstWeekChecklistContent ? (isRTL ? 'fa-caret-left' : 'fa-caret-right') : 'fa-caret-down'}`} style={{ marginInlineEnd: '8px', transition: 'transform 0.2s' }}></i>
                        <p className="sec-title" style={{ margin: '0', fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat' }}>{t('details.firstWeekChecklist')}</p>
                      </div>
                    </div>

                    <div id="firstWeekChecklistContent" style={{ display: collapsedSections.firstWeekChecklistContent ? 'none' : 'block', marginTop: '15px' }}>
                      {Object.keys(firstWeekChecklist.checklist || {}).map((day) => {
                        const dayTasks = firstWeekChecklist.checklist[day];

                        // Calculate day name based on start date
                        const getDayName = (dayKey) => {
                          const dayNumber = parseInt(dayKey.replace('day', ''), 10);
                          if (!firstWeekChecklist.startDate) {
                            // Fallback to static names if no start date
                            const fallbackWeekdays = {
                              1: t('details.weekdays.monday'),
                              2: t('details.weekdays.tuesday'),
                              3: t('details.weekdays.wednesday'),
                              4: t('details.weekdays.thursday'),
                              5: t('details.weekdays.friday')
                            };
                            return t('details.checklist.fallbackDayLabel', {
                              dayNumber: dayNumber || dayKey.replace('day', ''),
                              weekday: fallbackWeekdays[dayNumber] || dayKey
                            });
                          }

                          const dayIndex = (dayNumber || 1) - 1;
                          const baseDate = new Date(firstWeekChecklist.startDate);

                          // Calculate the actual date for this day, skipping weekends
                          let currentDate = new Date(baseDate);
                          let businessDaysAdded = 0;

                          while (businessDaysAdded < dayIndex) {
                            currentDate.setDate(currentDate.getDate() + 1);
                            // Skip weekends
                            if (currentDate.getDay() !== 0 && currentDate.getDay() !== 6) {
                              businessDaysAdded++;
                            }
                          }

                          const dayName = currentDate.toLocaleDateString(dateLocale || undefined, { weekday: 'long' });
                          const dateStr = currentDate.toLocaleDateString(dateLocale || undefined, { month: 'short', day: 'numeric' });

                          return t('details.checklist.dayLabel', { dayNumber: dayIndex + 1, weekday: dayName, date: dateStr });
                        };

                        const dayName = getDayName(day);

                        const completedTasks = dayTasks.filter((_, index) => checklistProgress[day]?.[index]).length;
                        const totalTasks = dayTasks.length;

                        return (
                          <div key={day} style={{
                            marginBottom: '20px',
                            padding: '15px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '8px',
                            border: '1px solid #e9ecef'
                          }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              marginBottom: '12px'
                            }}>
                              <h4 style={{
                                margin: 0,
                                fontSize: '16px',
                                fontWeight: '600',
                                color: 'var(--app-heading, #031128)',
                                fontFamily: 'Montserrat'
                              }}>
                                {dayName}
                              </h4>
                              <span style={{
                                fontSize: '12px',
                                color: completedTasks === totalTasks ? 'var(--app-tone-28a745, #28a745)' : '#568ded',
                                fontWeight: '600',
                                backgroundColor: completedTasks === totalTasks ? 'rgba(40, 167, 69, 0.15)' : 'var(--app-surface, rgba(86, 141, 237, 0.15))',
                                padding: '4px 10px',
                                borderRadius: '12px',
                                border: completedTasks === totalTasks ? '1px solid rgba(40, 167, 69, 0.3)' : "1px solid var(--app-border-strong, rgba(86, 141, 237, 0.3))"
                              }}>
                                {t('details.checklist.completedCount', { completed: completedTasks, total: totalTasks })}
                              </span>
                            </div>

                            {dayTasks.map((task, index) => (
                              <div key={index} style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                marginBottom: '10px',
                                padding: '10px 12px',
                                backgroundColor: '#fff',
                                borderRadius: '6px',
                                border: '1px solid #dee2e6',
                                minHeight: '40px'
                              }}>
                                <input
                                  type="checkbox"
                                  checked={checklistProgress[day]?.[index] || false}
                                  onChange={() => toggleChecklistTask(day, index)}
                                  style={{
                                    marginRight: '12px',
                                    marginTop: '2px',
                                    width: '18px',
                                    height: '18px',
                                    cursor: 'pointer',
                                    flexShrink: 0
                                  }}
                                />
                                <span style={{
                                  fontSize: '14px',
                                  color: checklistProgress[day]?.[index] ? '#6c757d' : 'var(--app-heading, #031128)',
                                  textDecoration: checklistProgress[day]?.[index] ? 'line-through' : 'none',
                                  fontFamily: 'Montserrat',
                                  lineHeight: '1.5',
                                  flex: 1,
                                  wordWrap: 'break-word'
                                }}>
                                  {task}
                                </span>
                              </div>
                            ))}
                          </div>
                        );
                      })}

                      <div style={{
                        marginTop: '15px',
                        padding: '12px',
                        backgroundColor: 'var(--app-surface, rgba(86, 141, 237, 0.08))',
                        borderRadius: '8px',
                        fontSize: '13px',
                        color: '#666',
                        borderLeft: '4px solid #568ded'
                      }}>
                        <div style={{ marginBottom: '5px' }}>
                          <strong>{t('details.checklist.created')}</strong> {new Date(firstWeekChecklist.createdAt).toLocaleDateString(dateLocale)}
                        </div>
                        {firstWeekChecklist.candidateEmail && (
                          <div>
                            <strong>{t('details.checklist.dailyEmails')}</strong> {t('details.checklist.dailyEmailsTo', { email: firstWeekChecklist.candidateEmail })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Candidate Analysis Section */}
                <div id="candidateAnalysisWrapper">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div onClick={() => toggleSection('candidate-analysis-sec')} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <i id="candidateAnalysisIcon" className={`fa-solid ${showAnalysis ? 'fa-caret-down' : (isRTL ? 'fa-caret-left' : 'fa-caret-right')}`} style={{ marginInlineEnd: '8px', transition: 'transform 0.2s' }}></i>
                      <p className="sec-title" style={{ margin: '0', fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat' }}>{t('details.candidateAnalysis')}</p>
                    </div>
                    {showAnalysis && (
                      <button id="analyzeBtn" onClick={handleAnalyze} disabled={isAnalyzing} style={{
                        color: '#568DED', borderRadius: '20px', fontWeight: "var(--app-weight-strong, bold)", fontSize: '20px', padding: '0',
                        backgroundColor: 'transparent', border: 'none', cursor: 'pointer'
                      }}  data-light-control={"quiet"}>
                        <i className={`fa-solid ${isAnalyzing ? 'fa-spin fa-rotate' : 'fa-rotate'}`}></i>
                      </button>
                    )}
                  </div>
                  {showAnalysis && (
                    <div id="candidate-analysis-sec" style={{ padding: '0 20px', marginTop: '20px' }}>
                      <div style={{
                        backgroundColor: '#F0F0F0', padding: '15px 20px', borderRadius: '12px',
                        width: '100%', height: '60vh', overflowY: 'scroll', border: '1px solid #e0e0e0'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '12px',
                          padding: '14px',
                          backgroundColor: '#fff',
                          border: '1px solid #dce5f2',
                          borderRadius: '8px',
                          marginBottom: '14px'
                        }}>
                          <div>
                            <div style={{ fontSize: '12px', color: '#6c757d', fontWeight: "var(--app-weight-strong, 700)", textTransform: 'none', letterSpacing: '0.04em' }}>
                              {t('details.analysis.fitScore')}
                            </div>
                            <div style={{ fontSize: '13px', color: '#6c757d', marginTop: '2px' }}>
                              {t('details.analysis.storedFitScore')}
                            </div>
                          </div>
                          <div style={{
                            fontSize: '32px',
                            fontWeight: "var(--app-weight-strong, 800)",
                            color: (getStoredFitScore() ?? 0) >= 75 ? 'var(--app-tone-198754, #198754)' : (getStoredFitScore() ?? 0) >= 55 ? 'var(--app-tone-d97706, #d97706)' : 'var(--app-tone-dc3545, #dc3545)',
                            lineHeight: 1
                          }}>
                            {getStoredFitScore() !== null ? `${getStoredFitScore()}%` : '--'}
                          </div>
                        </div>

                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                          gap: '8px',
                          marginBottom: '14px'
                        }}>
                          {[
                            { key: 'summary', label: t('details.analysis.tabs.summary') },
                            { key: 'interview', label: t('details.analysis.tabs.interviewQuestions') },
                            { key: 'benchmarks', label: t('details.analysis.tabs.benchmarks') }
                          ].map((tab) => (
                            <button
                              key={tab.key}
                              type="button"
                              onClick={() => setActiveAnalysisTab(tab.key)}
                              style={{
                                border: activeAnalysisTab === tab.key ? '1px solid #568ded' : '1px solid #d9e1ec',
                                backgroundColor: activeAnalysisTab === tab.key ? '#eaf2ff' : '#fff',
                                color: activeAnalysisTab === tab.key ? 'var(--app-tone-235fb2, #235fb2)' : '#415066',
                                borderRadius: '8px',
                                padding: '10px 8px',
                                fontSize: '13px',
                                fontWeight: "var(--app-weight-strong, 700)",
                                cursor: 'pointer',
                                fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat'
                              }}
                              data-light-control={activeAnalysisTab === tab.key ? "quiet" : "quiet"}>
                              {uiText(tab.label)}
                            </button>
                          ))}
                        </div>

                        {renderCandidateAnalysisTab()}
                      </div>
                    </div>
                  )}
                </div>

                {(applicationArtifacts.responseEntries.length > 0 || applicationArtifacts.coverLetterUrl || applicationArtifacts.portfolioLinks.length > 0) && (
                  <div id="applicationResponsesWrapper">
                    <div onClick={() => toggleSection('applicationResponsesContent')} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <i className={`fa-solid ${collapsedSections.applicationResponsesContent ? (isRTL ? 'fa-caret-left' : 'fa-caret-right') : 'fa-caret-down'}`} style={{ marginInlineEnd: '8px', transition: 'transform 0.2s' }}></i>
                      <p className="sec-title" style={{ margin: '0', fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat' }}>
                        {t('details.applicationResponses')}
                      </p>
                    </div>

                    {!collapsedSections.applicationResponsesContent && (
                      <div style={{ padding: '0 20px', marginTop: '20px' }}>
                        <div style={{
                          backgroundColor: '#F0F0F0',
                          padding: '15px 20px',
                          borderRadius: '12px',
                          width: '100%',
                          border: '1px solid #e0e0e0',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '14px'
                        }}>
                          {applicationArtifacts.coverLetterUrl && (
                            <div style={{
                              backgroundColor: '#fff',
                              border: '1px solid #ddd',
                              borderRadius: '10px',
                              padding: '12px 14px'
                            }}>
                              <div style={{ fontWeight: "var(--app-weight-strong, 700)", color: 'var(--app-heading, #031128)', marginBottom: '8px', fontFamily: 'Montserrat' }}>
                                {t('details.coverLetter')}
                              </div>
                              <a
                                href={applicationArtifacts.coverLetterUrl}
                                target="_blank"
                                rel="noreferrer"
                                style={{ color: '#568ded', fontWeight: 600, fontFamily: 'Montserrat', textDecoration: 'none' }}
                              >
                                <i className="fa-solid fa-file-pdf" style={{ marginInlineEnd: '7px' }}></i>
                                {t('details.viewCoverLetter')}
                              </a>
                            </div>
                          )}

                          {applicationArtifacts.portfolioLinks.length > 0 && (
                            <div style={{
                              backgroundColor: '#fff',
                              border: '1px solid #ddd',
                              borderRadius: '10px',
                              padding: '12px 14px'
                            }}>
                              <div style={{ fontWeight: "var(--app-weight-strong, 700)", color: 'var(--app-heading, #031128)', marginBottom: '8px', fontFamily: 'Montserrat' }}>
                                {t('details.portfolioLinks')}
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {applicationArtifacts.portfolioLinks.map((link, index) => (
                                  <a
                                    key={`${link}-${index}`}
                                    href={link}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{ color: '#568ded', fontWeight: 600, fontFamily: 'Montserrat', textDecoration: 'none', wordBreak: 'break-all' }}
                                  >
                                    <i className="fa-solid fa-up-right-from-square" style={{ marginInlineEnd: '7px' }}></i>
                                    {link}
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}

                          {applicationArtifacts.responseEntries.length > 0 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                              {applicationArtifacts.responseEntries.map((entry, index) => (
                                <div
                                  key={`${entry.label}-${index}`}
                                  style={{
                                    backgroundColor: '#fff',
                                    border: '1px solid #ddd',
                                    borderRadius: '10px',
                                    padding: '12px 14px'
                                  }}
                                >
                                  <div style={{ fontWeight: "var(--app-weight-strong, 700)", color: 'var(--app-heading, #031128)', marginBottom: '6px', fontFamily: 'Montserrat' }}>
                                    {uiText(entry.label)}
                                  </div>
                                  <div style={{ color: "var(--app-neutral-ink, #333)", lineHeight: '1.6', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'Montserrat' }}>
                                    {entry.answer}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* AI Assessment Sections */}
                {aiAssessmentSubstages && aiAssessmentSubstages.map((assessment, index) => (
                  <div key={assessment.id} id={`${assessment.id}Wrapper`}>
                    <div onClick={() => toggleSection(`assessment-${assessment.id}`)} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <i className={`fa-solid ${openAssessmentSections[`assessment-${assessment.id}`] ? 'fa-caret-down' : (isRTL ? 'fa-caret-left' : 'fa-caret-right')}`} style={{ marginInlineEnd: '8px', transition: 'transform 0.2s' }}></i>
                      <p className="sec-title" style={{ margin: '0', fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat' }}>{t('details.assessmentResults', { title: assessment.title })}</p>
                    </div>

                    {openAssessmentSections[`assessment-${assessment.id}`] && (
                      <div style={{ padding: '0 20px', marginTop: '20px' }}>
                        <div style={{
                          backgroundColor: '#F0F0F0',
                          padding: '15px 20px',
                          borderRadius: '12px',
                          width: '100%',
                          height: '60vh',
                          overflowY: 'scroll',
                          border: '1px solid #e0e0e0'
                        }}>
                          {loadingAssessmentData ? (
                            <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                              <i className="fa-solid fa-spinner fa-spin" style={{ marginInlineEnd: '8px' }}></i>
                              {t('details.loadingAssessmentData')}
                            </div>
                          ) : (
                            renderAssessmentContent(`${assessment.id}`)
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {/* Interview Section */}
                <div id="interviewWrapper">
                  <div onClick={() => toggleSection('interviewContent')} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <i className={`fa-solid ${showInterviews ? 'fa-caret-down' : (isRTL ? 'fa-caret-left' : 'fa-caret-right')}`} style={{ marginInlineEnd: '8px', transition: 'transform 0.2s' }}></i>
                    <p className="sec-title" style={{ margin: '0', fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat' }}>{t('details.scheduledInterviews')}</p>
                    {interviews.length > 0 && (
                      <span style={{
                        backgroundColor: '#568ded',
                        color: 'white',
                        borderRadius: '12px',
                        padding: '2px 8px',
                        fontSize: '12px',
                        marginLeft: '10px',
                        fontWeight: "var(--app-weight-strong, bold)"
                      }}>
                        {interviews.length}
                      </span>
                    )}
                  </div>

                  {showInterviews && (
                    <div id="interviewContent" style={{ padding: '0 20px', marginTop: '20px' }}>
                      <div style={{
                        backgroundColor: '#F0F0F0',
                        padding: '15px 20px',
                        borderRadius: '12px',
                        width: '100%',
                        maxHeight: '400px',
                        overflowY: 'auto',
                        border: '1px solid #e0e0e0'
                      }}>
                        {loadingInterviews ? (
                          <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                            <i className="fa-solid fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>{uiText("Loading interviews...")}</div>
                        ) : interviews.length > 0 ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {interviews.map((interview, index) => {
                              const status = getInterviewStatus(interview);
                              return (
                                <div key={interview.id} style={{
                                  backgroundColor: '#FFFFFF',
                                  border: '2px solid #E0E0E0',
                                  borderRadius: '12px',
                                  padding: '16px',
                                  position: 'relative'
                                }}>
                                  {/* Status Badge */}
                                  <div style={{
                                    position: 'absolute',
                                    top: '12px',
                                    right: '12px',
                                    backgroundColor: status.color,
                                    color: 'var(--app-text, white)',
                                    padding: '4px 8px',
                                    borderRadius: '8px',
                                    fontSize: '12px',
                                    fontWeight: "var(--app-weight-strong, bold)",
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                  }}>
                                    <i className={`fa-solid ${status.icon}`}></i>
                                    {status.text}
                                  </div>

                                  {/* Interview Title */}
                                  <h4 style={{
                                    margin: '0 0 12px 0',
                                    color: 'var(--app-heading, #031128)',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    paddingRight: '100px'
                                  }}>
                                    {interview.title}
                                  </h4>

                                  {/* Interview Details */}
                                  <div style={{ marginBottom: '12px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
                                      <i className="fa-solid fa-calendar" style={{ color: '#568ded', marginRight: '8px', width: '16px' }}></i>
                                      <span style={{ fontSize: '14px', color: '#666' }}>
                                        {formatInterviewDateTime(interview.date, interview.time)}
                                      </span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
                                      <i className="fa-solid fa-building" style={{ color: '#568ded', marginRight: '8px', width: '16px' }}></i>
                                      <span style={{ fontSize: '14px', color: '#666' }}>
                                        {interview.companyName || uiText("Company")}
                                      </span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
                                      <i className="fa-solid fa-clock" style={{ color: '#568ded', marginRight: '8px', width: '16px' }}></i>
                                      <span style={{ fontSize: '14px', color: '#666' }}>
                                        {interview.duration}{uiText(" minutes")}</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
                                      <i className="fa-solid fa-user-tie" style={{ color: '#568ded', marginRight: '8px', width: '16px' }}></i>
                                      <span style={{ fontSize: '14px', color: '#666' }}>
                                        {interview.interviewer}
                                      </span>
                                    </div>
                                    {interview.meetingLink && (
                                      <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <i className="fa-solid fa-video" style={{ color: '#568ded', marginRight: '8px', width: '16px' }}></i>
                                        <a
                                          href={interview.meetingLink}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{
                                            fontSize: '14px',
                                            color: '#568ded',
                                            textDecoration: 'none',
                                            fontWeight: '500'
                                          }}
                                        >{uiText("Join Meeting")}</a>
                                      </div>
                                    )}
                                  </div>

                                  {/* Action Buttons */}
                                  {getInterviewStatus(interview).text === 'Scheduled' && (
                                    <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                                      <button
                                        onClick={() => handleRescheduleInterview(interview)}
                                        style={{ '--light-control-fill': '#568ded',
                                          backgroundColor: '#568ded',
                                          color: 'white',
                                          border: 'none',
                                          padding: '8px 12px',
                                          borderRadius: '6px',
                                          fontSize: '12px',
                                          fontWeight: '500',
                                          cursor: 'pointer',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '4px'
                                        }}
                                        data-light-control={"filled"}>
                                        <i className="fa-solid fa-calendar-days"></i>{uiText("Reschedule")}</button>
                                      <button
                                        onClick={() => handleCancelInterview(interview)}
                                        style={{ '--light-control-fill': '#dc3545',
                                          backgroundColor: '#dc3545',
                                          color: 'white',
                                          border: 'none',
                                          padding: '8px 12px',
                                          borderRadius: '6px',
                                          fontSize: '12px',
                                          fontWeight: '500',
                                          cursor: 'pointer',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '4px'
                                        }}
                                        data-light-control={"filled"}>
                                        <i className="fa-solid fa-times"></i>{uiText("Cancel")}</button>
                                      {interview.meetingLink && (
                                        <button
                                          onClick={() => window.open(interview.meetingLink, '_blank')}
                                          style={{ '--light-control-fill': '#28a745',
                                            backgroundColor: '#28a745',
                                            color: 'white',
                                            border: 'none',
                                            padding: '8px 12px',
                                            borderRadius: '6px',
                                            fontSize: '12px',
                                            fontWeight: '500',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                          }}
                                          data-light-control={"filled"}>
                                          <i className="fa-solid fa-external-link-alt"></i>{uiText("Join Now")}</button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#666' }}>
                            <i className="fa-solid fa-calendar-xmark" style={{ fontSize: '32px', marginBottom: '12px', color: 'var(--app-text, #ccc)' }}></i>
                            <p style={{ margin: '0', fontSize: '16px' }}>{uiText("No interviews scheduled yet")}</p>
                            <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>{uiText("Move this candidate to an interview substage to schedule an interview")}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>


                {/* Scorecard Section - Show only for authenticated users on left side */}
                {applicantData.applicantType !== 'imported' && (
                  <div id="scorecardWrapper">
                    <div onClick={() => toggleSection('scorecardContent')} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <i id="scorecardIcon" className={`fa-solid ${showScorecard ? 'fa-caret-down' : (isRTL ? 'fa-caret-left' : 'fa-caret-right')}`} style={{ marginInlineEnd: '8px', transition: 'transform 0.2s' }}></i>
                      <p className="sec-title" style={{ margin: '0', fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat' }}>{t('details.scorecardChat')}</p>
                    </div>

                    {showScorecard && (
                      <div id="scorecardContent" style={{ marginTop: '10px' }}>
                        <p style={{ fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat', fontSize: '16px', margin: '0', fontWeight: "var(--app-weight-strong, bold)" }}>{t('details.rating')}</p>
                        <div id="ratingButtonsContainer" style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                          {['Poor', 'Below Average', 'Average', 'Good', 'Excellent'].map(rating => (
                            <button
                              key={rating}
                              className={`rating-btn ${selectedRating === rating ? 'selected' : ''}`}
                              data-score={rating}
                              onClick={() => handleRatingSelect(rating)}
                              style={rating === 'Poor' ? { [isRTL ? 'marginLeft' : 'marginLeft']: '4px' } : {}}
                            >
                              {t(`ratings.${rating.replace(/\s+/g, '')}`)}
                            </button>
                          ))}
                        </div>
                        <div style={{ marginTop: '10px', color: 'var(--app-heading, #031128)', fontSize: '14px' }}>
                          {ratingSummary.count > 0 ? (
                            <span>
                              {t('details.teamAverage')} <strong>{t(`ratings.${ratingSummary.label?.replace(/\s+/g, '')}`)}</strong> ({ratingSummary.avg.toFixed(1)}) • {ratingSummary.count} {t('details.countOfRatings')}
                            </span>
                          ) : (
                            <span style={{ color: '#6c757d' }}>{t('details.noTeamRatings')}</span>
                          )}
                        </div>
                        {/* Team Ratings overview */}
                        <div style={{ marginTop: '16px' }}>
                          <p style={{ fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat', fontSize: '16px', margin: '0 0 6px 0', fontWeight: "var(--app-weight-strong, bold)" }}>{t('details.teamRatings')}</p>
                          <div>
                            {Object.keys(teamRatings).length === 0 && (
                              <div style={{ color: '#666', fontSize: '14px' }}>{t('details.noTeamRatings')}</div>
                            )}
                            {Object.entries(teamRatings).map(([uid, entry]) => {
                              const member = teamDirectory.byUid[uid] || {};
                              const name = member.name || entry.userEmail || uiText("Team Member");
                              const photoUrl = member.photoUrl || '';
                              return (
                                <div key={uid} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                                  <div style={{ width: 28, height: 28, borderRadius: '50%', overflow: 'hidden', background: '#e7eefc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {photoUrl ? (
                                      <img src={photoUrl} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    ) : (
                                      <span style={{ fontSize: 12, color: '#568ded', fontWeight: "var(--app-weight-strong, 700)" }}>{(name || 'T').slice(0, 1).toUpperCase()}</span>
                                    )}
                                  </div>
                                  <div style={{ fontSize: '14px', color: 'var(--app-heading, #031128)' }}>
                                    <strong>{name}</strong>: {entry.rating}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Chat with Team */}
                        <div style={{ marginTop: '20px' }}>
                          <p style={{ fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat', fontSize: '16px', margin: '0', fontWeight: "var(--app-weight-strong, bold)" }}>{t('details.chatWithTeam')}</p>
                          <div id="notesContainer" style={{ maxHeight: '28vh', overflowY: 'auto', paddingRight: '6px', marginTop: '8px' }}>
                            {teamNotes.map((note) => {
                              const member = (note.uid && teamDirectory.byUid[note.uid]) || teamDirectory.byEmail[note.user] || {};
                              const name = member.name || note.user || uiText("Team Member");
                              const photoUrl = member.photoUrl || '';
                              return (
                                <div key={note.id} style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'flex-start' }}>
                                  <div style={{ width: 28, height: 28, borderRadius: '50%', overflow: 'hidden', background: '#e7eefc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {photoUrl ? (
                                      <img src={photoUrl} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    ) : (
                                      <span style={{ fontSize: 12, color: '#568ded', fontWeight: "var(--app-weight-strong, 700)" }}>{(name || 'T').slice(0, 1).toUpperCase()}</span>
                                    )}
                                  </div>
                                  <div style={{ background: '#F7F9FC', border: '1px solid #E0E7FF', borderRadius: 8, padding: '8px 10px', flex: 1 }}>
                                    <div style={{ fontSize: 12, color: '#5f6b7a', marginBottom: 4 }}>
                                      <strong style={{ color: 'var(--app-heading, #031128)' }}>{name}</strong>
                                      {note.timestamp && (
                                        <span style={{ marginLeft: 6, color: '#94a3b8' }}>{new Date(note.timestamp).toLocaleString()}</span>
                                      )}
                                    </div>
                                    <div style={{ fontSize: 14, color: 'var(--app-heading, #031128)', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{note.content}</div>
                                  </div>
                                </div>
                              );
                            })}
                            {teamNotes.length === 0 && (
                              <div style={{ color: '#666', fontSize: '14px' }}>{t('details.noTeamMessages')}</div>
                            )}
                          </div>
                          <div style={{
                            backgroundColor: '#568ded', padding: '0px 20px', margin: '14px 0', display: 'flex',
                            borderRadius: '20px', alignItems: 'center', boxShadow: '0px 0px 7px rgba(0, 0, 0, 0.15)'
                          }}>
                            <textarea
                              id="team-messageBox"
                              ref={teamMessageBoxRef}
                              value={newNoteContent}
                              onChange={(e) => setNewNoteContent(e.target.value)}
                              placeholder={t('details.typeTeamMessage')}
                              style={{
                                flexGrow: 1, padding: '10px', border: 'none', resize: 'none', fontSize: '16px', height: '40px',
                                backgroundColor: 'transparent', outline: 'none', color: '#ffffff', fontFamily: 'Montserrat',
                                maxHeight: '200px', overflowY: 'auto'
                              }}
                              rows={1}
                            />
                            <button onClick={handleAddNote} disabled={!newNoteContent.trim()} style={{ '--light-control-fill': '#568ded',
                              backgroundColor: '#568ded', border: 'none', width: '45px', height: '45px', borderRadius: '50%',
                              marginLeft: '10px', opacity: newNoteContent.trim() ? '1' : '0.5',
                              cursor: newNoteContent.trim() ? 'pointer' : 'default', color: '#FFF', alignSelf: 'flex-end'
                            }}  data-light-control={"filled"}>
                              <i className="fa-regular fa-paper-plane"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Right Column - Messaging or Scorecard for Imported */}
              <div style={{ width: '48%' }}>
                {applicantData.applicantType === 'imported' ? (
                  // Show Scorecard & Team Notes for imported candidates
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'start', gap: '7px' }}>
                      <p className="sec-title">{t('details.scorecardChat')}</p>
                    </div>
                    <div style={{ backgroundColor: '#F0F0F0', padding: '15px 20px', borderRadius: '12px', height: '60vh', overflowY: 'auto', marginTop: '10px' }}>
                      <p style={{ fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat', fontSize: '16px', margin: '0', fontWeight: "var(--app-weight-strong, bold)" }}>{t('details.rating')}</p>
                      <div id="ratingButtonsContainer" style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                        {['Poor', 'Below Average', 'Average', 'Good', 'Excellent'].map(rating => (
                          <button
                            key={rating}
                            className={`rating-btn ${selectedRating === rating ? 'selected' : ''}`}
                            data-score={rating}
                            onClick={() => handleRatingSelect(rating)}
                            style={rating === 'Poor' ? { [isRTL ? 'marginLeft' : 'marginLeft']: '4px' } : {}}
                          >
                            {t(`ratings.${rating.replace(/\s+/g, '')}`)}
                          </button>
                        ))}
                      </div>
                      <div style={{ marginTop: '10px', color: 'var(--app-heading, #031128)', fontSize: '14px' }}>
                        {ratingSummary.count > 0 ? (
                          <span>
                            {t('details.teamAverage')} <strong>{t(`ratings.${ratingSummary.label?.replace(/\s+/g, '')}`)}</strong> ({ratingSummary.avg.toFixed(1)}) • {ratingSummary.count} {t('details.countOfRatings')}
                          </span>
                        ) : (
                          <span style={{ color: '#6c757d' }}>{t('details.noTeamRatings')}</span>
                        )}
                      </div>

                      <div style={{ marginTop: '20px' }}>
                        <p style={{ fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat', fontSize: '16px', margin: '0', fontWeight: "var(--app-weight-strong, bold)" }}>{t('details.chatWithTeam')}</p>
                        <div id="notesContainer" style={{ maxHeight: '34vh', overflowY: 'auto', paddingRight: '6px', marginTop: '8px' }}>
                          {teamNotes.map((note) => {
                            const member = (note.uid && teamDirectory.byUid[note.uid]) || teamDirectory.byEmail[note.user] || {};
                            const name = member.name || note.user || uiText("Team Member");
                            const photoUrl = member.photoUrl || '';
                            return (
                              <div key={note.id} style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'flex-start' }}>
                                <div style={{ width: 28, height: 28, borderRadius: '50%', overflow: 'hidden', background: '#e7eefc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  {photoUrl ? (
                                    <img src={photoUrl} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                  ) : (
                                    <span style={{ fontSize: 12, color: '#568ded', fontWeight: "var(--app-weight-strong, 700)" }}>{(name || 'T').slice(0, 1).toUpperCase()}</span>
                                  )}
                                </div>
                                <div style={{ background: '#FFFFFF', border: '1px solid #E0E7FF', borderRadius: 8, padding: '8px 10px', flex: 1 }}>
                                  <div style={{ fontSize: 12, color: '#5f6b7a', marginBottom: 4 }}>
                                    <strong style={{ color: 'var(--app-heading, #031128)' }}>{name}</strong>
                                    {note.timestamp && (
                                      <span style={{ marginLeft: 6, color: '#94a3b8' }}>{new Date(note.timestamp).toLocaleString()}</span>
                                    )}
                                  </div>
                                  <div style={{ fontSize: 14, color: 'var(--app-heading, #031128)', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{note.content}</div>
                                </div>
                              </div>
                            );
                          })}
                          {teamNotes.length === 0 && (
                            <div style={{ color: '#666', fontSize: '14px' }}>{uiText("No messages yet. Start the conversation below.")}</div>
                          )}
                        </div>
                        <div style={{
                          backgroundColor: '#568ded', padding: '0px 20px', margin: '14px 0', display: 'flex',
                          borderRadius: '20px', alignItems: 'center', boxShadow: '0px 0px 7px rgba(0, 0, 0, 0.15)'
                        }}>
                          <textarea
                            id="team-messageBox"
                            ref={teamMessageBoxRef}
                            value={newNoteContent}
                            onChange={(e) => setNewNoteContent(e.target.value)}
                            placeholder={t('details.typeTeamMessage')}
                            style={{
                              flexGrow: 1, padding: '10px', border: 'none', resize: 'none', fontSize: '16px', height: '40px',
                              backgroundColor: 'transparent', outline: 'none', color: '#ffffff', fontFamily: 'Montserrat',
                              maxHeight: '200px', overflowY: 'auto'
                            }}
                            rows={1}
                          />
                          <button onClick={handleAddNote} disabled={!newNoteContent.trim()} style={{ '--light-control-fill': '#568ded',
                            backgroundColor: '#568ded', border: 'none', width: '45px', height: '45px', borderRadius: '50%',
                            marginLeft: '10px', opacity: newNoteContent.trim() ? '1' : '0.5',
                            cursor: newNoteContent.trim() ? 'pointer' : 'default', color: '#FFF', alignSelf: 'flex-end'
                          }}  data-light-control={"filled"}>
                            <i className="fa-regular fa-paper-plane"></i>
                          </button>
                        </div>
                      </div>

                      {/* Future Email Templates Section - Commented Out */}
                      {/*
                  <div style={{marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #ddd'}}>
                    <p style={{fontFamily: 'Montserrat', fontSize: '16px', margin: '0', fontWeight: 'bold'}}>Email Templates</p>
                    <div style={{marginTop: '10px', padding: '15px', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #ddd'}}>
                      <p style={{color: '#666', fontSize: '14px', margin: '0'}}>
                        <i className="fa-solid fa-envelope" style={{marginRight: '8px'}}></i>
                        Email templates for imported candidates coming soon...
                      </p>
                    </div>
                  </div>
                  */}
                    </div>
                  </div>
                ) : (
                  // Show Messaging for authenticated candidates
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'start', gap: '7px' }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <p className="sec-title">{t('details.candidateMessaging')}</p>
                      </div>
                      <div style={{ position: 'relative' }}>
                        <button id="msg-info"
                          onClick={() => setShowTooltip(!showTooltip)}
                          style={{
                            color: '#568DED', borderRadius: '20px', fontWeight: "var(--app-weight-strong, bold)", fontSize: '20px', padding: '0',
                            backgroundColor: 'transparent', border: 'none', cursor: 'pointer'
                          }}  data-light-control={"quiet"}>
                          <i className="fa-solid fa-info-circle"></i>
                        </button>
                        {showTooltip && (
                          <div className="tooltip" style={{
                            position: 'absolute',
                            top: '25px',
                            left: '-150px',
                            backgroundColor: '#333',
                            color: 'var(--app-text, white)',
                            padding: '10px',
                            borderRadius: '8px',
                            fontSize: '14px',
                            zIndex: 1000,
                            width: '300px',
                            boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
                          }}>
                            {t('details.messagingTooltip')}
                            <div style={{
                              position: 'absolute',
                              top: '-5px',
                              left: '155px',
                              width: '10px',
                              height: '10px',
                              backgroundColor: '#333',
                              transform: 'rotate(45deg)'
                            }}></div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div id="appl-msg-pop-sec" style={{
                      backgroundColor: '#F0F0F0', padding: '10px 15px', borderRadius: '12px', display: 'flex',
                      flexDirection: 'column', height: '60vh', marginTop: '10px'
                    }}>
                      <div id="msg-list-app" ref={messagesRef} style={{ flexGrow: 1, overflowY: 'auto', padding: '20px' }}>
                        {messages.map((message, index) => (
                          <div key={index} id={`msg-${message.id}`} className={`message ${message.user === userUID ? 'local-person' : 'other-person'}`}>
                            <p style={{ overflowWrap: "break-word", wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
                              {message.message}
                            </p>
                          </div>
                        ))}
                      </div>
                      <div style={{
                        backgroundColor: '#568ded', padding: '0px 20px', margin: '20px 30px', display: 'flex',
                        borderRadius: '20px', alignItems: 'center', boxShadow: '0px 0px 7px rgba(0, 0, 0, 0.25)'
                      }}>
                        <textarea id="messageBox-appl" ref={messageBoxRef} value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          onKeyDown={handleKeyDown}
                          placeholder={t('details.typeMessageToCandidate')} style={{
                            flexGrow: 1, padding: '10px', border: 'none', resize: 'none', fontSize: '16px', height: '40px',
                            backgroundColor: 'transparent', outline: 'none', color: '#ffffff', fontFamily: 'Montserrat',
                            maxHeight: '200px', overflowY: 'auto'
                          }} />
                        <button id="sendmsgbtn-appl" onClick={handleSendMessage} disabled={!newMessage.trim()} style={{ '--light-control-fill': '#568ded',
                          backgroundColor: '#568ded', border: 'none', width: '45px', height: '45px', borderRadius: '50%',
                          marginLeft: '10px', opacity: newMessage.trim() ? '1' : '0.5',
                          cursor: newMessage.trim() ? 'pointer' : 'default', color: '#FFF', alignSelf: 'flex-end'
                        }}  data-light-control={"filled"}>
                          <i className="fa-regular fa-paper-plane"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Interview Scheduler Modal */}
        {showInterviewScheduler && (
          <InterviewScheduler
            applicantId={applicantId}
            userUID={userUID}
            onClose={() => {
              setShowInterviewScheduler(false);
              setSelectedInterviewForReschedule(null);
            }}
            onScheduleSuccess={handleInterviewScheduleSuccess}
            jobTitle={applicantData.jobTitle || selectedJobDescription?.title || 'Position'}
            companyName={companyName || 'Our Company'}
            existingInterview={selectedInterviewForReschedule}
            substageTitle={selectedInterviewForReschedule?.title || ''}
          />
        )}
      </div>
      {/* Reject Modal */}
      {showRejectModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '560px' }}>
            <div className="modal-header">
              <h2>{t('details.rejectCandidate')}</h2>
              <button className="close" onClick={closeRejectModal}>&times;</button>
            </div>
            <div className="form-section" style={{ padding: '24px 32px' }}>
              <p style={{ marginTop: 0, color: 'var(--app-heading, #031128)' }}>
                {t('details.rejectConfirmMessage')}
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={closeRejectModal} disabled={rejectInProgress}>{t('workspace.cancel')}</button>
              <div>
                <button className="btn-primary" onClick={handleRejectAndNotify} disabled={rejectInProgress}>
                  {t('details.sendRejection')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicantDetailsPopup; 
